import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import AccountCoverageTimeline from "../components/accounts/AccountCoverageTimeline";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import {
  Container,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Box,
  Alert,
  CircularProgress,
  Collapse,
  TextField,
  Card,
  CardContent,
  Chip,
  Snackbar,
  IconButton,
  Tooltip,
  Link,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Badge,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  StackedBarChart as StackedBarChartIcon,
  Timeline as TimelineIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  FilterAlt as FilterAltIcon,
  Payment as PaymentIcon,
  Lock as LockIcon,
  History as HistoryIcon,
  SmartToy as SmartToyIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Business as BusinessIcon,
  Domain as DomainIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { usePlaidLink } from "react-plaid-link";
import PlaidUpdateLauncher, {
  PlaidUpdateLauncherHandle,
} from "../components/plaid/PlaidUpdateLauncher";
import {
  plaidService,
  PlaidAccount,
  PlaidTransaction,
  PlaidSyncStatus,
  CSVTransaction,
  ImportHistoryEntry,
  stripeConnectService,
  StripeAccount,
  StripeTransaction,
  ManualTransactionCount,
  AccountTransactionStats,
  organizationService,
  Organization,
} from "../services/external";
import { PlaidTestInfo } from "../components/accounts";
import ConnectChecklist from "../components/accounts/ConnectChecklist";
import { settingsService } from "../services/settings/settingsService";
import { groupAPI, shareAPI } from "../services/api";
import { demoService } from "../services/demo/demoService";
import FloatingActionMenu from "../components/common/FloatingActionMenu";
import { ManageSharesModal } from "../components/groups";
import { Group } from "../lib/types";
import { formatImportedAt } from "../lib/dateUtils";
import { parseTransactionsCsv } from "../utils/parseTransactionsCsv";
import { agentService } from "../services/external/agentService";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Wallet/card providers that can't be linked through Plaid's credential login
// (their banking partners only expose logins to direct bank customers). They are
// added as manual accounts and populated via CSV import — same path as Cash App.
interface ManualWalletProvider {
  id: "cash_app" | "venmo" | "lyft_direct" | "uber_card";
  label: string;
  defaultName: string;
  partnerNote: string;
  csvSource: string;
  // Direct link to the provider's transactions/statement page, when one exists
  // publicly. Rendered as a button in the create dialog. Omitted where the
  // export only lives inside a native app (Lyft Direct, Uber Card).
  transactionsUrl?: string;
}
const MANUAL_WALLET_PROVIDERS: ManualWalletProvider[] = [
  {
    id: "cash_app",
    label: "Cash App",
    defaultName: "Cash App",
    partnerNote:
      "Cash App can't be connected with a login. Its banking partner, Lincoln Savings Bank, only offers a login to actual bank customers — Cash App users don't have one.",
    csvSource:
      "In Cash App (or on cash.app in a browser), open your Activity and export/download a CSV statement.",
    transactionsUrl: "https://cash.app/account/activity",
  },
  {
    id: "venmo",
    label: "Venmo",
    defaultName: "Venmo",
    partnerNote:
      "Venmo can't be connected with a login here. Its balance is held through Bancorp Bank, which doesn't offer Venmo users a bank login.",
    csvSource:
      "On venmo.com, open Statement, pick a date range, and download the CSV.",
    transactionsUrl: "https://account.venmo.com/statement",
  },
  {
    id: "lyft_direct",
    label: "Lyft Direct",
    defaultName: "Lyft Direct",
    partnerNote:
      "The Lyft Direct card (issued by Stride Bank through Payfare) usually can't be linked by login. Add it as a tracked account instead.",
    csvSource:
      "In the Lyft Direct app or Payfare portal, export your transaction history as a CSV.",
  },
  {
    id: "uber_card",
    label: "Uber Card",
    defaultName: "Uber Card",
    partnerNote:
      "The Uber Pro Card (issued by GoBank / Green Dot) usually can't be linked by login. Add it as a tracked account instead.",
    csvSource:
      "In the Uber Pro Card app (GoBank), export your transaction history as a CSV.",
  },
];

// Get calendar year date range
const getCurrentCalendarYear = () => {
  const year = new Date().getFullYear();
  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  };
};

const TIMELINE_LEGEND_GROUP_ORDER = [
  "Savings",
  "Checking",
  "Credit Cards",
  "Other",
];

// Convert ALL-CAPS account names (common from Plaid, e.g. "PLAID CHECKING") to
// Proper Case, while preserving already-mixed-case names and short acronyms
// like HSA / LLC / IRA (uppercase tokens of length <= 3).
const toProperCase = (name: string): string =>
  (name || "").replace(/[\p{L}][\p{L}']*/gu, (word) =>
    word.length > 3 && word === word.toUpperCase()
      ? word.charAt(0) + word.slice(1).toLowerCase()
      : word
  );

// Stable account identifier. Real Plaid accounts expose `account_id`; demo
// fixtures use `id`. Prefer account_id, fall back to id.
const acctId = (a: PlaidAccount): string =>
  a.account_id || (a as unknown as { id?: string }).id || "";

// Plaid OAuth link_token persistence.
//
// During an OAuth bank handoff (e.g. Uber Pro Card) the browser leaves our
// origin and returns to the redirect URI. The link_token MUST survive that
// round-trip — if it's gone on return, Plaid Link rejects the OAuth handoff and
// silently restarts the institution picker, producing the "phone → code →
// password → back to phone" infinite loop. sessionStorage is too fragile here:
// Safari ITP and in-app/embedded browsers routinely clear it across the
// cross-domain hop. We persist in localStorage (durable, first-party, survives
// the redirect back to our origin) with a short TTL, and keep sessionStorage as
// a best-effort mirror / back-compat read.
const PLAID_LINK_TOKEN_KEY = "plaid_link_token";
const PLAID_LINK_TOKEN_TS_KEY = "plaid_link_token_ts";
const PLAID_LINK_TOKEN_TTL_MS = 30 * 60 * 1000; // OAuth state is short-lived

const persistPlaidLinkToken = (token: string): void => {
  try {
    localStorage.setItem(PLAID_LINK_TOKEN_KEY, token);
    localStorage.setItem(PLAID_LINK_TOKEN_TS_KEY, String(Date.now()));
  } catch (e) {
    console.debug("[DEBUG] persistPlaidLinkToken: localStorage failed", e);
  }
  try {
    sessionStorage.setItem(PLAID_LINK_TOKEN_KEY, token);
  } catch {
    /* best-effort mirror */
  }
};

const readPlaidLinkToken = (): string | null => {
  try {
    const ts = Number(localStorage.getItem(PLAID_LINK_TOKEN_TS_KEY) || 0);
    const token = localStorage.getItem(PLAID_LINK_TOKEN_KEY);
    if (token && ts && Date.now() - ts < PLAID_LINK_TOKEN_TTL_MS) {
      return token;
    }
  } catch (e) {
    console.debug("[DEBUG] readPlaidLinkToken: localStorage failed", e);
  }
  try {
    return sessionStorage.getItem(PLAID_LINK_TOKEN_KEY);
  } catch {
    return null;
  }
};

const clearPlaidLinkToken = (): void => {
  try {
    localStorage.removeItem(PLAID_LINK_TOKEN_KEY);
    localStorage.removeItem(PLAID_LINK_TOKEN_TS_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

const AccountsPage: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageView, setPageView] = useState<"accounts" | "timeline">(() =>
    searchParams.get("view") === "timeline" ? "timeline" : "accounts"
  );
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [sharedOnly, setSharedOnly] = useState(false);
  // How the Accounts container groups/sorts accounts: by Organization (default)
  // or by financial institution / provider ("Account" tab). Persisted to
  // localStorage so the choice survives reloads.
  const accountsGroupByKey = "accountsGroupBy";
  const [accountsGroupBy, setAccountsGroupBy] = useState<
    "organization" | "account"
  >(() => {
    try {
      const stored = localStorage.getItem(accountsGroupByKey);
      return stored === "account" ? "account" : "organization";
    } catch {
      return "organization";
    }
  });
  const handleAccountsGroupByChange = useCallback(
    (value: "organization" | "account") => {
      setAccountsGroupBy(value);
      try {
        localStorage.setItem(accountsGroupByKey, value);
      } catch {
        /* ignore persistence failures (private mode, etc.) */
      }
    },
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plaidExitIssue, setPlaidExitIssue] = useState<{
    status?: string | null;
    metadata?: any;
    error?: any;
    occurredAt: string;
  } | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [itemsNeedingUpdate, setItemsNeedingUpdate] = useState<
    Array<{
      item_id: string;
      institution_name: string | null;
      error_code: string;
    }>
  >([]);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);
  // Map of itemId → pre-minted update-mode link token. Tokens are fetched
  // up-front when items-needing-update loads so that clicking "Update X"
  // can call open() *synchronously inside the click handler* — Plaid Link
  // silently no-op's open() when it isn't called from a user gesture.
  const [updateTokensByItem, setUpdateTokensByItem] = useState<
    Record<string, string>
  >({});
  const updateLauncherRefs = useRef<
    Record<string, PlaidUpdateLauncherHandle | null>
  >({});
  // Map of itemId → account-selection (account_selection_enabled) update-mode
  // link token. These power the per-institution "Edit Plaid connection" button
  // so the user can add/enable accounts that were never selected during the
  // original Link flow (e.g. a Chase Savings account). Minted on demand the
  // first time the user clicks the button for that institution.
  const [editConnectionTokensByItem, setEditConnectionTokensByItem] = useState<
    Record<string, string>
  >({});
  const editConnectionLauncherRefs = useRef<
    Record<string, PlaidUpdateLauncherHandle | null>
  >({});
  // itemId currently minting/launching an edit-connection flow (button spinner).
  const [editingConnectionItemId, setEditingConnectionItemId] = useState<
    string | null
  >(null);
  const [oauthRedirectUri, setOauthRedirectUri] = useState<string | null>(
    () => {
      const params = new URLSearchParams(window.location.search);
      return params.has("oauth_state_id") ? window.location.href : null;
    }
  );
  const oauthAutoOpenedRef = useRef(false);
  const [showTestInfo, setShowTestInfo] = useState(true);
  const [plaidEnvironment, setPlaidEnvironment] = useState<
    "sandbox" | "production"
  >("production");

  // Stripe state
  const [stripeAccounts, setStripeAccounts] = useState<StripeAccount[]>([]);
  const [stripeTransactions, setStripeTransactions] = useState<
    StripeTransaction[]
  >([]);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAllPassword, setDeleteAllPassword] = useState("");
  const [deletingAllAccounts, setDeletingAllAccounts] = useState(false);

  // Add Account modal state
  const [isAddAccountDialogOpen, setIsAddAccountDialogOpen] = useState(false);
  const [isPaymentProcessorExpanded, setIsPaymentProcessorExpanded] =
    useState(false);
  // The manual wallet provider whose create dialog is open (null = closed).
  const [activeWalletProvider, setActiveWalletProvider] =
    useState<ManualWalletProvider | null>(null);
  // Manual-account create form (shared by all wallet providers)
  const [walletName, setWalletName] = useState("");
  const [walletBalance, setWalletBalance] = useState("");
  const [walletMask, setWalletMask] = useState("");
  const [creatingWallet, setCreatingWallet] = useState(false);
  // Edit-balance dialog for an existing manual account
  const [editingManualAccount, setEditingManualAccount] =
    useState<PlaidAccount | null>(null);
  const [manualEditName, setManualEditName] = useState("");
  const [manualEditBalance, setManualEditBalance] = useState("");
  const [savingManualAccount, setSavingManualAccount] = useState(false);

  // Date range state (from URL params or defaults to current calendar year)
  const { startDate: defaultStart, endDate: defaultEnd } =
    getCurrentCalendarYear();
  const [startDate, setStartDate] = useState(
    searchParams.get("start") || defaultStart
  );
  const [endDate, setEndDate] = useState(searchParams.get("end") || defaultEnd);
  // Pending dates for the date picker (don't filter until user clicks apply)
  const [pendingStartDate, setPendingStartDate] = useState(startDate);
  const [pendingEndDate, setPendingEndDate] = useState(endDate);
  const [transactions, setTransactions] = useState<PlaidTransaction[]>([]);
  const [totalDeposits, setTotalDeposits] = useState<number>(0);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [datePreset, setDatePreset] = useState<string>("");
  const [chartStyle, setChartStyle] = useState<
    "curved" | "straight" | "bar" | "area"
  >("bar");
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    new Set()
  );
  // Collapsed organization accordions (empty = all expanded by default).
  const [collapsedOrgs, setCollapsedOrgs] = useState<Set<string>>(new Set());
  // Hidden accounts in the timeline chart (persisted in URL as slugs)
  const [hiddenChartAccounts, setHiddenChartAccounts] = useState<Set<string>>(
    new Set()
  );
  // Timeline tab selection: "all" | "checking" | "savings" | "credit"
  const [timelineTab, setTimelineTab] = useState<
    "all" | "checking" | "savings" | "credit"
  >("checking");

  const timelineLegendGroupMap = useMemo(() => {
    const groupMap = new Map<string, string>();

    accounts.forEach((account) => {
      let group = "Other";

      if (account.type === "credit") {
        group = "Credit Cards";
      } else if (account.subtype === "savings") {
        group = "Savings";
      } else if (
        account.subtype === "checking" ||
        account.type === "depository"
      ) {
        group = "Checking";
      }

      groupMap.set(account.account_id, group);
    });

    stripeAccounts.forEach((stripeAccount) => {
      groupMap.set(`stripe_${stripeAccount.stripe_user_id}`, "Other");
    });

    return groupMap;
  }, [accounts, stripeAccounts]);

  const buildTimelineLegendLabels = useCallback(
    (chart: any) => {
      const defaultLabels =
        ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
      const groupedLabels: Record<string, any[]> = {
        Savings: [],
        Checking: [],
        "Credit Cards": [],
        Other: [],
      };

      defaultLabels.forEach((item: any) => {
        const dataset = chart.data.datasets[item.datasetIndex];
        const accountId = dataset?.accountId as string | undefined;
        const group = accountId
          ? timelineLegendGroupMap.get(accountId) || "Other"
          : "Other";
        groupedLabels[group]?.push(item);
      });

      const labels: any[] = [];

      TIMELINE_LEGEND_GROUP_ORDER.forEach((group) => {
        const groupItems = groupedLabels[group];
        if (!groupItems || groupItems.length === 0) return;

        labels.push(...groupItems);
      });

      return labels;
    },
    [timelineLegendGroupMap]
  );

  // Date preset options
  const getDateRangeForPreset = (
    preset: string
  ): { start: string; end: string } | null => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    const dayOfWeek = today.getDay();

    const formatDate = (d: Date) => d.toISOString().split("T")[0];

    switch (preset) {
      case "ytd": // Year to Date
        return {
          start: `${year}-01-01`,
          end: formatDate(today),
        };
      case "last_year": // Last Year
        return {
          start: `${year - 1}-01-01`,
          end: `${year - 1}-12-31`,
        };
      case "this_month": // This Month
        return {
          start: `${year}-${String(month + 1).padStart(2, "0")}-01`,
          end: formatDate(today),
        };
      case "this_week": // This Week (Sunday to today)
        const weekStart = new Date(today);
        weekStart.setDate(day - dayOfWeek);
        return {
          start: formatDate(weekStart),
          end: formatDate(today),
        };
      case "today": // Today
        return {
          start: formatDate(today),
          end: formatDate(today),
        };
      default:
        return null;
    }
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);
    const range = getDateRangeForPreset(preset);
    console.debug("[AccountsPage][QuickSelect] preset changed", {
      preset,
      range,
    });
    if (range) {
      // Update the input fields...
      setPendingStartDate(range.start);
      setPendingEndDate(range.end);
      // ...AND commit immediately so Start/End inputs reflect the preset and the
      // Total Deposits effect (deps: [startDate, endDate, ...]) re-runs. Without
      // this commit the preset only set the pending values and required a manual
      // Apply click, leaving dates/total stale.
      setStartDate(range.start);
      setEndDate(range.end);
      console.debug("[AccountsPage][QuickSelect] applied date range", {
        startDate: range.start,
        endDate: range.end,
      });
    }
  };

  // Apply pending date filters
  const handleApplyDateFilter = () => {
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
  };

  // Check if pending dates differ from applied dates
  const hasUnappliedDateChanges =
    pendingStartDate !== startDate || pendingEndDate !== endDate;

  // Add Account menu handlers
  const handleAddAccountClick = () => {
    console.debug("[DEBUG] Opening Add Account dialog");
    setIsAddAccountDialogOpen(true);
  };

  const handleAddAccountClose = () => {
    console.debug("[DEBUG] Closing Add Account dialog");
    setIsAddAccountDialogOpen(false);
    setIsPaymentProcessorExpanded(false);
  };

  const handlePaymentProcessorToggle = () => {
    console.debug("[DEBUG] Toggling payment processor list");
    setIsPaymentProcessorExpanded((prev) => !prev);
  };

  const handleAddBank = () => {
    handleAddAccountClose();
    console.debug("[DEBUG] Add Account selection: bank");
    open();
  };

  const handleWalletProviderSelect = (provider: ManualWalletProvider) => {
    console.debug("[DEBUG] Add Account selection: wallet_modal", {
      provider: provider.id,
    });
    handleAddAccountClose();
    setWalletName(provider.defaultName);
    setWalletBalance("");
    setWalletMask("");
    setActiveWalletProvider(provider);
  };

  // Create the wallet account as a manual (non-Plaid) account, then open the
  // CSV import dialog so the user can immediately add their transaction history.
  // A copyable prompt the user hands to their own AI agent to add this wallet
  // via the Focus: Finance MCP server (create_manual_wallet → import_wallet_csv).
  const walletAgentPrompt = (provider: ManualWalletProvider) => {
    const source = provider.transactionsUrl
      ? `Download my transactions CSV from ${provider.transactionsUrl}`
      : provider.csvSource;
    return [
      "You have the Focus: Finance MCP server (tools: create_manual_wallet, import_wallet_csv, connection_status).",
      `Help me add my ${provider.label} account:`,
      `1. Call create_manual_wallet with provider "${provider.id}".`,
      `2. ${source}`,
      "3. Call import_wallet_csv with the new account_id and the CSV contents.",
    ].join("\n");
  };

  const handleCopyWalletHandoff = async () => {
    if (!activeWalletProvider) return;
    try {
      await navigator.clipboard.writeText(
        walletAgentPrompt(activeWalletProvider)
      );
      setSnackbar({
        open: true,
        message: `Copied — paste it to your AI agent to add ${activeWalletProvider.label}.`,
        severity: "success",
      });
    } catch (err) {
      console.error("[DEBUG] Failed to copy wallet hand-off", err);
      setSnackbar({
        open: true,
        message: "Couldn't copy to clipboard",
        severity: "error",
      });
    }
  };

  const handleCreateWalletAccount = async () => {
    if (creatingWallet || !activeWalletProvider) return;
    const provider = activeWalletProvider;
    const name = walletName.trim() || provider.defaultName;
    try {
      setCreatingWallet(true);
      console.debug("[DEBUG] Creating manual wallet account", {
        provider: provider.id,
        name,
        hasBalance: walletBalance !== "",
      });
      const account = await plaidService.createManualAccount({
        name,
        balance: walletBalance === "" ? null : walletBalance,
        provider: provider.id,
        mask: walletMask.trim() || undefined,
      });
      await loadAccounts();
      setActiveWalletProvider(null);
      setSnackbar({
        open: true,
        message: `${provider.label} account added. Import your transaction history from a ${provider.label} CSV export.`,
        severity: "success",
      });
      // Jump straight into CSV import for the new account.
      setImportingAccount(account);
    } catch (err) {
      console.error("[DEBUG] Failed to create wallet account", err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : `Failed to create ${provider.label} account`,
        severity: "error",
      });
    } finally {
      setCreatingWallet(false);
    }
  };

  const openEditManualAccount = (account: PlaidAccount) => {
    setEditingManualAccount(account);
    setManualEditName(account.name || "");
    setManualEditBalance(
      account.balances?.current != null ? String(account.balances.current) : ""
    );
  };

  const handleSaveManualAccount = async () => {
    if (!editingManualAccount || savingManualAccount) return;
    try {
      setSavingManualAccount(true);
      await plaidService.updateManualAccount(editingManualAccount.account_id, {
        name: manualEditName.trim() || undefined,
        balance: manualEditBalance === "" ? null : manualEditBalance,
      });
      await loadAccounts();
      setEditingManualAccount(null);
      setSnackbar({
        open: true,
        message: "Account updated",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : "Failed to update account",
        severity: "error",
      });
    } finally {
      setSavingManualAccount(false);
    }
  };

  const handleDeleteManualAccount = async (account: PlaidAccount) => {
    if (
      !window.confirm(
        `Remove "${account.name}" and its imported transactions? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await plaidService.deleteManualAccount(account.account_id);
      await loadAccounts();
      setSnackbar({
        open: true,
        message: "Account removed",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : "Failed to remove account",
        severity: "error",
      });
    }
  };

  const handleSelectPaymentProcessor = (processor: string) => {
    handleAddAccountClose();
    console.debug("[DEBUG] Add Account selection: payment_processor", {
      processor,
    });
    if (processor === "stripe") {
      handleConnectStripe();
    }
  };

  // Sync status state
  const [syncStatus, setSyncStatus] = useState<PlaidSyncStatus[]>([]);
  const [manualImportByAccount, setManualImportByAccount] = useState<
    Record<string, string>
  >({});
  const [manualTransactionCounts, setManualTransactionCounts] = useState<
    Record<string, ManualTransactionCount>
  >({});
  const [accountTransactionStats, setAccountTransactionStats] = useState<
    Record<string, AccountTransactionStats>
  >({});
  const [showSyncStatus, setShowSyncStatus] = useState(false);
  const [loadingSyncStatus, setLoadingSyncStatus] = useState(false);
  const [refreshingItems, setRefreshingItems] = useState<Set<string>>(
    new Set()
  );
  // Per-account refresh spinners (keyed by account_id), independent of the
  // institution-level `refreshingItems` so a single-account refresh only spins
  // that one row's icon.
  const [refreshingAccounts, setRefreshingAccounts] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (oauthRedirectUri) {
      console.debug("[DEBUG] Plaid OAuth redirect detected", {
        redirectUri: oauthRedirectUri,
      });
      const storedToken = readPlaidLinkToken();
      if (storedToken) {
        console.debug("[DEBUG] Restoring Plaid link token for OAuth resume", {
          hasToken: true,
        });
        setLinkToken(storedToken);
      }
    }
  }, [oauthRedirectUri]);

  // Custom account names state
  const [customNames, setCustomNames] = useState<Record<string, string>>({});
  const [editingAccount, setEditingAccount] = useState<PlaidAccount | null>(
    null
  );
  const [editNameValue, setEditNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Organizations state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [accountOrganizations, setAccountOrganizations] = useState<
    Record<string, string>
  >({});
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  // Manage-sharing modal (org / bank / account scope).
  const [manageShare, setManageShare] = useState<{
    title: string;
    accountIds: string[];
  } | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const openManageShare = useCallback((title: string, accountIds: string[]) => {
    const ids = Array.from(new Set(accountIds.filter(Boolean)));
    if (ids.length === 0) return;
    setManageShare({ title, accountIds: ids });
    // Lazy-load groups (no side effects inside a state updater).
    groupAPI
      .list()
      .then((g) => setGroups((prev) => (prev.length === 0 ? g : prev)))
      .catch((e) => console.error("Failed to load groups:", e));
  }, []);
  const [orgName, setOrgName] = useState("");
  const [orgDescription, setOrgDescription] = useState("");
  const [orgColor, setOrgColor] = useState("#1976d2");
  const [savingOrg, setSavingOrg] = useState(false);

  // CSV Import state
  const [importingAccount, setImportingAccount] = useState<PlaidAccount | null>(
    null
  );
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<CSVTransaction[]>([]);
  const [importHistoryAccount, setImportHistoryAccount] =
    useState<PlaidAccount | null>(null);
  const [importHistory, setImportHistory] = useState<ImportHistoryEntry[]>([]);
  const [loadingImportHistory, setLoadingImportHistory] = useState(false);

  // Local-agent auto-import: which account currently has a job running, and a
  // short human-readable status for the button tooltip.
  const [agentJobAccountId, setAgentJobAccountId] = useState<string | null>(
    null
  );
  const [agentJobStatus, setAgentJobStatus] = useState<string>("");

  // Transactions viewer modal state
  const [transactionsViewerOpen, setTransactionsViewerOpen] = useState(false);
  const [transactionsViewerData, setTransactionsViewerData] = useState<
    PlaidTransaction[]
  >([]);
  const [transactionsViewerTitle, setTransactionsViewerTitle] = useState("");
  const [transactionsViewerLoading, setTransactionsViewerLoading] =
    useState(false);
  const [highlightTransactionDate, setHighlightTransactionDate] = useState<
    string | null
  >(null);

  // Transactions viewer sorting and filtering state
  type SortColumn =
    | "date"
    | "name"
    | "merchant_name"
    | "category"
    | "amount"
    | "source";
  type SortDirection = "asc" | "desc";
  const [txnSortColumn, setTxnSortColumn] = useState<SortColumn>("date");
  const [txnSortDirection, setTxnSortDirection] =
    useState<SortDirection>("desc");
  const [txnFilterSource, setTxnFilterSource] = useState<string>("");
  const [txnFilterMerchant, setTxnFilterMerchant] = useState<string>("");
  const [txnFilterCategory, setTxnFilterCategory] = useState<string>("");
  // Free-text search + date-range + pagination for the transactions viewer.
  const [txnSearch, setTxnSearch] = useState<string>("");
  const [txnDateFrom, setTxnDateFrom] = useState<string>("");
  const [txnDateTo, setTxnDateTo] = useState<string>("");
  const [txnPage, setTxnPage] = useState<number>(0);
  const [txnRowsPerPage, setTxnRowsPerPage] = useState<number>(25);
  const [txnJumpPage, setTxnJumpPage] = useState<string>("");

  // How many people each account is shared with (for the share-button badge).
  const [shareCountsByAccount, setShareCountsByAccount] = useState<
    Record<string, number>
  >({});
  // The distinct group ids each account is shared with, so a group/org summary
  // row can union them into a single de-duplicated share count.
  const [shareGroupIdsByAccount, setShareGroupIdsByAccount] = useState<
    Record<string, string[]>
  >({});

  const isDemoMode = demoService.isDemo();
  const hasConnectedAccounts = accounts.length > 0 || stripeAccounts.length > 0;

  // Load Stripe accounts
  const loadStripeAccounts = useCallback(async () => {
    try {
      setLoadingStripe(true);
      const accounts = await stripeConnectService.getAccounts();
      setStripeAccounts(accounts);
    } catch (err) {
      console.error("Error loading Stripe accounts:", err);
    } finally {
      setLoadingStripe(false);
    }
  }, []);

  // Load custom account names
  const loadCustomNames = useCallback(async () => {
    try {
      const names = await plaidService.getCustomNames();
      setCustomNames(names);
    } catch (err) {
      console.error("Error loading custom names:", err);
    }
  }, []);

  // Load organizations
  const loadOrganizations = useCallback(async () => {
    try {
      const [orgs, mapping] = await Promise.all([
        organizationService.getAll(),
        organizationService.getAccountOrganizations(),
      ]);
      setOrganizations(orgs);
      setAccountOrganizations(mapping);
    } catch (err) {
      console.error("Error loading organizations:", err);
    }
  }, []);

  // Load all transactions and calculate deposits
  const loadTransactions = useCallback(async () => {
    if (!startDate || !endDate) return;

    try {
      setLoadingTransactions(true);

      // Load Plaid transactions
      const plaidTxns = await plaidService.getTransactions(startDate, endDate);
      setTransactions(plaidTxns);

      // Load Stripe transactions
      let stripeTxns: StripeTransaction[] = [];
      if (stripeAccounts.length > 0) {
        stripeTxns = await stripeConnectService.getTransactions(
          startDate,
          endDate
        );
        setStripeTransactions(stripeTxns);
      }

      // Calculate total deposits (excluding transfers)
      // Plaid: negative amounts = money in
      const isTransferTxn = (t: PlaidTransaction): boolean => {
        const name = (t.name || "").toLowerCase();
        const merchantName = (t.merchant_name || "").toLowerCase();
        const categories = [
          ...(t.internal_category || []),
          ...(Array.isArray(t.category)
            ? t.category
            : t.category
              ? [t.category]
              : []),
        ].map((c) => c.toLowerCase());
        const excludeKeywords = [
          "transfer",
          "xfer",
          "zelle",
          "venmo",
          "paypal",
          "cash app",
          "reversal",
          "refund",
          "chargeback",
        ];
        const hasExcludeKeyword = excludeKeywords.some(
          (keyword) => name.includes(keyword) || merchantName.includes(keyword)
        );
        const hasExcludeCategory = categories.some(
          (cat) =>
            cat.includes("transfer") ||
            cat.includes("refund") ||
            cat.includes("reversal") ||
            cat === "transfer_in" ||
            cat === "transfer_out"
        );
        return hasExcludeKeyword || hasExcludeCategory;
      };

      const isDepositTxn = (t: PlaidTransaction): boolean => {
        const amount =
          typeof t.amount === "number" ? t.amount : Number(t.amount);
        const isManual = t.source === "manual";
        const isInflow = isManual ? amount > 0 : amount < 0;
        return isInflow && !isTransferTxn(t);
      };

      const plaidDeposits = plaidTxns
        .filter(isDepositTxn)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      // Stripe: positive amounts = money in (customer payments)
      const stripeDeposits = stripeTxns
        .filter((t) => t.status === "succeeded" && !t.refunded)
        .reduce((sum, t) => sum + t.amount, 0);

      const depositCandidates = plaidTxns.filter((t) => {
        const amount =
          typeof t.amount === "number" ? t.amount : Number(t.amount);
        const isManual = t.source === "manual";
        return isManual ? amount > 0 : amount < 0;
      });
      const excludedDeposits = depositCandidates.filter(isTransferTxn);
      console.log("Accounts deposits debug", {
        totalTransactions: plaidTxns.length,
        depositCandidates: depositCandidates.length,
        excludedDeposits: excludedDeposits.length,
        depositTotal: plaidDeposits,
        stripeDepositTotal: stripeDeposits,
        sampleExcluded: excludedDeposits.slice(0, 5).map((t) => ({
          name: t.name,
          merchant: t.merchant_name,
          amount: t.amount,
          category: t.category,
          internal_category: t.internal_category,
          source: t.source,
        })),
      });

      setTotalDeposits(plaidDeposits + stripeDeposits);
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [startDate, endDate, stripeAccounts.length]);

  // Handle Stripe callback URL params
  useEffect(() => {
    const stripeConnected = searchParams.get("stripe_connected");
    const stripeError = searchParams.get("stripe_error");

    if (stripeConnected === "true") {
      setSnackbar({
        open: true,
        message: "Stripe account connected successfully!",
        severity: "success",
      });
      loadStripeAccounts();
      // Clear URL params
      setSearchParams({});
    } else if (stripeError) {
      setSnackbar({
        open: true,
        message: `Failed to connect Stripe: ${stripeError}`,
        severity: "error",
      });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, loadStripeAccounts]);

  // Load sync status for accounts
  const loadSyncStatus = useCallback(async () => {
    try {
      setLoadingSyncStatus(true);
      const response = await plaidService.getSyncStatus();
      setSyncStatus(response.items);
      setManualTransactionCounts(response.manualTransactionCounts);
      setAccountTransactionStats(response.accountTransactionStats || {});
    } catch (err) {
      console.error("Error loading sync status:", err);
    } finally {
      setLoadingSyncStatus(false);
    }
  }, []);

  // Latest manual-import timestamp per account (real CSV/manual import dates).
  const loadManualImportTimestamps = useCallback(async () => {
    try {
      const latest = await plaidService.getLatestImportTimestamps();
      setManualImportByAccount(latest);
    } catch (err) {
      console.error("Error loading manual import timestamps:", err);
    }
  }, []);

  // Load sync status after accounts are loaded
  useEffect(() => {
    if (accounts.length > 0) {
      loadSyncStatus();
      loadManualImportTimestamps();
    }
  }, [accounts.length, loadSyncStatus, loadManualImportTimestamps]);

  // Load transactions when dates change
  useEffect(() => {
    if (accounts.length > 0 || stripeAccounts.length > 0) {
      loadTransactions();
    }
  }, [
    startDate,
    endDate,
    accounts.length,
    stripeAccounts.length,
    loadTransactions,
  ]);

  // Sync date range to URL params
  useEffect(() => {
    const currentStart = searchParams.get("start");
    const currentEnd = searchParams.get("end");
    // Only update if values have changed to avoid loops
    if (currentStart !== startDate || currentEnd !== endDate) {
      const params = new URLSearchParams(searchParams);
      params.set("start", startDate);
      params.set("end", endDate);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Build mappings between account IDs and slugs (for URL persistence)
  const accountSlugMappings = useMemo(() => {
    const slugify = (name: string): string => {
      return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .substring(0, 30);
    };

    const idToSlug: Record<string, string> = {};
    const slugToId: Record<string, string> = {};

    // Map all Plaid accounts
    accounts.forEach((account) => {
      const displayName = customNames[account.account_id] || account.name;
      let slug = slugify(displayName);
      // Handle duplicates by appending last 4 chars of account ID
      if (slugToId[slug]) {
        slug = `${slug}-${account.account_id.slice(-4)}`;
      }
      idToSlug[account.account_id] = slug;
      slugToId[slug] = account.account_id;
    });

    // Map all Stripe accounts
    stripeAccounts.forEach((account) => {
      let slug = slugify(account.business_name || account.id);
      if (slugToId[slug]) {
        slug = `${slug}-${account.id.slice(-4)}`;
      }
      idToSlug[account.id] = slug;
      slugToId[slug] = account.id;
    });

    return { idToSlug, slugToId };
  }, [accounts, stripeAccounts, customNames]);

  // Initialize hidden accounts from URL once mappings are ready
  useEffect(() => {
    const hidden = searchParams.get("hidden");
    if (hidden && Object.keys(accountSlugMappings.slugToId).length > 0) {
      const slugs = hidden.split(",");
      const accountIds = slugs
        .map((slug) => accountSlugMappings.slugToId[slug])
        .filter(Boolean);
      setHiddenChartAccounts(new Set(accountIds));
    }
  }, [searchParams, accountSlugMappings.slugToId]);

  // Toggle chart account visibility and update URL
  const toggleChartAccountVisibility = useCallback(
    (accountId: string) => {
      setHiddenChartAccounts((prev) => {
        const newHidden = new Set(prev);
        if (newHidden.has(accountId)) {
          newHidden.delete(accountId);
        } else {
          newHidden.add(accountId);
        }

        // Update URL with slugs instead of account IDs
        const params = new URLSearchParams(searchParams);
        if (newHidden.size > 0) {
          const slugs = Array.from(newHidden)
            .map((id) => accountSlugMappings.idToSlug[id])
            .filter(Boolean);
          params.set("hidden", slugs.join(","));
        } else {
          params.delete("hidden");
        }
        setSearchParams(params, { replace: true });

        return newHidden;
      });
    },
    [searchParams, setSearchParams, accountSlugMappings.idToSlug]
  );

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Loading accounts");
      const accountsData = await plaidService.getAccounts();
      console.log("Loaded accounts", { count: accountsData.length });
      setAccounts(accountsData);
      setError(null);
    } catch (err) {
      console.error("Error loading accounts:", err);
      setError("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearOauthRedirect = useCallback(() => {
    if (searchParams.has("oauth_state_id")) {
      const params = new URLSearchParams(searchParams);
      params.delete("oauth_state_id");
      setSearchParams(params, { replace: true });
    }
    clearPlaidLinkToken();
    setOauthRedirectUri(null);
    oauthAutoOpenedRef.current = false;
    console.debug("[DEBUG] Cleared Plaid OAuth redirect state");
  }, [searchParams, setSearchParams]);

  const createLinkToken = useCallback(async () => {
    try {
      if (oauthRedirectUri) {
        const cachedToken = readPlaidLinkToken();
        if (cachedToken) {
          console.debug("[DEBUG] Using cached Plaid link token for OAuth flow");
          setLinkToken(cachedToken);
          return;
        }
        // We are returning from a Plaid OAuth redirect (URL has
        // oauth_state_id) but the original link_token is gone from
        // sessionStorage (e.g. Safari ITP wiped storage across the
        // cross-domain bank handoff). Minting a NEW token here and passing
        // it with receivedRedirectUri makes Plaid Link reject the OAuth
        // handoff and silently restart the institution flow — the classic
        // "phone → code → password → back to phone" infinite loop. Bail out
        // of OAuth mode instead: drop the redirect params/state so the next
        // open() starts a clean, fresh Link session.
        console.warn(
          "[AccountsPage] OAuth redirect detected but original link_token missing from sessionStorage; aborting OAuth resume to avoid restart loop"
        );
        clearOauthRedirect();
        const freshToken = await plaidService.createLinkToken();
        setLinkToken(freshToken);
        persistPlaidLinkToken(freshToken);
        return;
      }
      const token = await plaidService.createLinkToken();
      setLinkToken(token);
      persistPlaidLinkToken(token);
    } catch (err) {
      console.error("Error creating link token:", err);
      setError("Failed to initialize account linking");
    }
  }, [oauthRedirectUri, clearOauthRedirect]);

  const loadSettings = useCallback(async () => {
    try {
      const settings = await settingsService.getUserSettings();
      setPlaidEnvironment(settings.plaid_environment);

      // Plaid credentials are now system-level (managed in .env)
      // Just load accounts and create link token
      await loadAccounts();
      await createLinkToken();
    } catch (err) {
      console.error("Error loading settings:", err);
      setError("Failed to load settings");
      setLoading(false);
    }
  }, [createLinkToken, loadAccounts]);

  const loadItemsNeedingUpdate = useCallback(async () => {
    try {
      const items = await plaidService.itemsNeedingUpdate();
      setItemsNeedingUpdate(items);
      console.log("[AccountsPage] items needing update", {
        count: items.length,
      });
      const tokens: Record<string, string> = {};
      await Promise.all(
        items.map(async (it) => {
          try {
            tokens[it.item_id] = await plaidService.createLinkToken({
              itemId: it.item_id,
            });
          } catch (e) {
            console.warn("[AccountsPage] failed to pre-fetch update token", {
              itemId: it.item_id,
              err: e,
            });
          }
        })
      );
      setUpdateTokensByItem(tokens);
    } catch (err) {
      console.warn("[AccountsPage] failed to load items needing update", err);
    }
  }, []);

  const handleUpdateItem = useCallback((itemId: string) => {
    const handle = updateLauncherRefs.current[itemId];
    if (!handle || !handle.ready) {
      console.warn("[AccountsPage] launcher not ready yet", {
        itemId,
        hasHandle: !!handle,
        ready: handle?.ready,
      });
      setError(
        "Plaid Link isn't ready yet. Refresh the page in a moment and try again."
      );
      return;
    }
    setUpdatingItemId(itemId);
    // Call open() synchronously inside the click handler so the browser
    // treats it as a user gesture; otherwise Plaid Link silently no-op's.
    handle.open();
  }, []);

  // Load settings and accounts on component mount
  useEffect(() => {
    loadSettings();
    loadStripeAccounts();
    loadCustomNames();
    loadOrganizations();
    loadItemsNeedingUpdate();
  }, [
    loadSettings,
    loadStripeAccounts,
    loadCustomNames,
    loadOrganizations,
    loadItemsNeedingUpdate,
  ]);

  // NOTE: usePlaidLink destroys & recreates its underlying handler whenever
  // its config object reference changes. If onSuccess/onExit/onEvent are
  // bare functions (new ref each render) and an unrelated state change
  // happens while Link is open, the iframe can be torn down mid-flow —
  // observed as Link "closing repeatedly". useCallback + useMemo below.
  const onSuccess = useCallback(
    async (public_token: string, metadata: any) => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      try {
        const isUpdateMode = !public_token;
        console.debug("[DEBUG] Plaid Link success", {
          institution: metadata?.institution,
          accountsCount: metadata?.accounts?.length || 0,
          isUpdateMode,
        });
        if (!isUpdateMode) {
          await plaidService.exchangePublicToken(public_token);
        }
        await loadAccounts();
        await loadItemsNeedingUpdate();
        clearOauthRedirect();
      } catch (err) {
        console.error("Error handling Plaid Link success:", err);
        setError("Failed to finish account linking");
      }
    },
    [loadAccounts, loadItemsNeedingUpdate, clearOauthRedirect]
  );

  const onExit = useCallback(
    (err: any, metadata: any) => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      if (err != null) {
        console.error("Plaid Link exited with error:", err);
      }
      console.debug("[DEBUG] Plaid Link exited", {
        error: err,
        error_code: err?.error_code,
        error_type: err?.error_type,
        error_message: err?.error_message,
        status: metadata?.status,
        exitStatus: metadata?.exit_status,
        institution: metadata?.institution,
        request_id: metadata?.request_id,
        link_session_id: metadata?.link_session_id,
      });
      try {
        console.debug(
          "[DEBUG] Plaid Link exit details",
          JSON.stringify(metadata)
        );
      } catch (error) {
        console.debug(
          "[DEBUG] Plaid Link exit details stringify failed",
          error
        );
      }
      if (metadata?.exit_status === "requires_credentials") {
        setPlaidExitIssue({
          status: metadata?.exit_status || metadata?.status,
          metadata,
          error: err,
          occurredAt: new Date().toISOString(),
        });
      } else {
        setPlaidExitIssue(null);
      }
      clearOauthRedirect();
    },
    [clearOauthRedirect]
  );

  const onEvent = useCallback((eventName: string, metadata: any) => {
    // Surface the key fields that pinpoint where an OAuth flow bounces:
    // view_name (e.g. SELECT_INSTITUTION → ... → back to start indicates a
    // restart loop) and any error_code/error_type Plaid reports.
    console.debug("[DEBUG] Plaid Link event", {
      eventName,
      view_name: metadata?.view_name,
      error_code: metadata?.error_code,
      error_type: metadata?.error_type,
      institution_name: metadata?.institution_name,
      request_id: metadata?.request_id,
      link_session_id: metadata?.link_session_id,
    });
    try {
      console.debug(
        "[DEBUG] Plaid Link event details",
        JSON.stringify(metadata)
      );
    } catch (error) {
      console.debug("[DEBUG] Plaid Link event details stringify failed", error);
    }
  }, []);

  const emptyPlaidSuccess = useCallback(() => {}, []);
  const plaidConfig = useMemo(
    () =>
      linkToken
        ? {
            token: linkToken,
            onSuccess,
            onExit,
            onEvent,
            env: plaidEnvironment as any,
            receivedRedirectUri: oauthRedirectUri || undefined,
          }
        : { token: null, onSuccess: emptyPlaidSuccess },
    [
      linkToken,
      onSuccess,
      onExit,
      onEvent,
      plaidEnvironment,
      oauthRedirectUri,
      emptyPlaidSuccess,
    ]
  );

  const { open, ready } = usePlaidLink(plaidConfig);

  // Update-mode lifecycle is owned by the <PlaidUpdateLauncher /> instances
  // mounted below — one per item needing update. We pre-mint a token for
  // each item up-front so the click handler can call open() inside the
  // user-gesture window. These callbacks just reset the "Starting…"
  // button state and refresh the item list when the user finishes.
  const handleUpdateLauncherOpened = useCallback(() => {
    setUpdatingItemId(null);
  }, []);
  const handleUpdateLauncherExited = useCallback(() => {
    setUpdatingItemId(null);
    loadItemsNeedingUpdate();
    loadAccounts();
  }, [loadItemsNeedingUpdate, loadAccounts]);

  // "Edit Plaid connection" — launch Plaid Link in account-selection update
  // mode for a single institution's item so the user can add/enable accounts
  // that were never selected during the original Link flow. Plaid Link's
  // open() must run inside the user-gesture window, but we may need to mint the
  // token first (async). The pattern: if the token + ready launcher already
  // exist, open() synchronously now; otherwise mint the token, mount the
  // launcher, and auto-open once it becomes ready (handled by the effect
  // below). This mirrors the OAuth auto-open behavior.
  const handleEditConnection = useCallback(
    async (itemId: string) => {
      if (!itemId) return;
      console.log("[AccountsPage] edit Plaid connection requested", { itemId });
      const existingHandle = editConnectionLauncherRefs.current[itemId];
      if (existingHandle?.ready) {
        console.log(
          "[AccountsPage] edit-connection launcher ready, opening now",
          { itemId }
        );
        setEditingConnectionItemId(null);
        existingHandle.open();
        return;
      }
      setEditingConnectionItemId(itemId);
      if (editConnectionTokensByItem[itemId]) {
        // Token exists but launcher not ready yet — the auto-open effect will
        // fire once it is. Nothing more to do here.
        console.log(
          "[AccountsPage] edit-connection token present, awaiting launcher ready",
          { itemId }
        );
        return;
      }
      try {
        const token = await plaidService.createLinkToken({
          itemId,
          accountSelection: true,
        });
        console.log("[AccountsPage] edit-connection token minted", {
          itemId,
          hasToken: Boolean(token),
        });
        setEditConnectionTokensByItem((prev) => ({ ...prev, [itemId]: token }));
        // Auto-open handled by the effect watching launcher readiness.
      } catch (err) {
        console.error("[AccountsPage] failed to mint edit-connection token", {
          itemId,
          err,
        });
        setEditingConnectionItemId(null);
        setError(
          "Couldn't start editing this connection. Please try again in a moment."
        );
      }
    },
    [editConnectionTokensByItem]
  );

  const handleEditConnectionFinished = useCallback(() => {
    console.log(
      "[AccountsPage] edit-connection flow finished, refetching accounts"
    );
    setEditingConnectionItemId(null);
    loadAccounts();
    loadItemsNeedingUpdate();
  }, [loadAccounts, loadItemsNeedingUpdate]);

  useEffect(() => {
    if (!oauthRedirectUri) {
      oauthAutoOpenedRef.current = false;
      return;
    }
    if (ready && !oauthAutoOpenedRef.current) {
      oauthAutoOpenedRef.current = true;
      console.debug("[DEBUG] Opening Plaid Link after OAuth redirect");
      open();
    }
  }, [oauthRedirectUri, ready, open]);

  // Auto-open of a freshly-minted edit-connection launcher is handled in the
  // launcher's ref callback (it re-runs when the handle becomes ready) — see
  // the <PlaidUpdateLauncher /> edit-connection instances rendered below.

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Shared Chart.js tooltip config for the "Monthly Deposits by Account" chart.
  // Shows the formatted deposit amount + account (dataset) name on hover, and
  // respects the active MUI light/dark theme. See CLAUDE.md console-debug convention.
  const depositsChartTooltip = {
    enabled: true,
    mode: "index" as const,
    intersect: false,
    backgroundColor: isDarkMode
      ? "rgba(33, 33, 33, 0.95)"
      : "rgba(255, 255, 255, 0.97)",
    titleColor: isDarkMode ? "#fff" : "#1a1a1a",
    bodyColor: isDarkMode ? "#e0e0e0" : "#333",
    borderColor: isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
    borderWidth: 1,
    padding: 12,
    boxPadding: 4,
    usePointStyle: true,
    callbacks: {
      label: (ctx: any) => {
        const accountName = ctx.dataset?.label ?? "Account";
        const raw = typeof ctx.parsed?.y === "number" ? ctx.parsed.y : 0;
        const formatted = formatCurrency(raw);
        console.debug("[DepositsByAccount][tooltip] hover", {
          account: accountName,
          month: ctx.label,
          rawAmount: raw,
          formatted,
        });
        return ` ${accountName}: ${formatted}`;
      },
    },
  };

  const getAccountTypeDisplay = (type: string, subtype: string) => {
    return `${type.charAt(0).toUpperCase() + type.slice(1)} - ${
      subtype.charAt(0).toUpperCase() + subtype.slice(1)
    }`;
  };

  // Check if a transaction is a transfer/reversal/refund (should be excluded from deposits)
  const isTransfer = useCallback((t: PlaidTransaction): boolean => {
    const name = (t.name || "").toLowerCase();
    const merchantName = (t.merchant_name || "").toLowerCase();
    const categories = [
      ...(t.internal_category || []),
      ...(Array.isArray(t.category)
        ? t.category
        : t.category
          ? [t.category]
          : []),
    ].map((c) => c.toLowerCase());

    // Check for transfer/reversal/refund keywords in name or merchant
    const excludeKeywords = [
      "transfer",
      "xfer",
      "zelle",
      "venmo",
      "paypal",
      "cash app",
      "reversal",
      "refund",
      "chargeback",
    ];
    const hasExcludeKeyword = excludeKeywords.some(
      (keyword) => name.includes(keyword) || merchantName.includes(keyword)
    );

    // Check for transfer/reversal/refund in categories
    const hasExcludeCategory = categories.some(
      (cat) =>
        cat.includes("transfer") ||
        cat.includes("refund") ||
        cat.includes("reversal") ||
        cat === "transfer_in" ||
        cat === "transfer_out"
    );

    return hasExcludeKeyword || hasExcludeCategory;
  }, []);

  const isDeposit = useCallback(
    (t: PlaidTransaction): boolean => {
      const amount = typeof t.amount === "number" ? t.amount : Number(t.amount);
      const isManual = t.source === "manual";
      const isInflow = isManual ? amount > 0 : amount < 0;
      return isInflow && !isTransfer(t);
    },
    [isTransfer]
  );

  // Calculate deposits for a specific account (excluding transfers)
  const getAccountDeposits = (accountId: string): number => {
    return transactions
      .filter((t) => t.account_id === accountId && isDeposit(t))
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
  };

  // Get deposit transactions for a specific account (for tooltip, excluding transfers)
  const getAccountDepositTransactions = (
    accountId: string
  ): PlaidTransaction[] => {
    return transactions
      .filter((t) => t.account_id === accountId && isDeposit(t))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  // Render deposits tooltip content
  const renderDepositsTooltip = (accountId: string) => {
    const deposits = getAccountDepositTransactions(accountId);
    if (deposits.length === 0) return "No deposits in this period";

    // Split into columns (max 15 per column)
    const maxPerColumn = 15;
    const numColumns = Math.min(4, Math.ceil(deposits.length / maxPerColumn));
    const itemsPerColumn = Math.ceil(deposits.length / numColumns);

    const columns: PlaidTransaction[][] = [];
    for (let i = 0; i < numColumns; i++) {
      columns.push(
        deposits.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn)
      );
    }

    return (
      <Box sx={{ display: "flex", gap: 2, p: 1, maxWidth: 800 }}>
        {columns.map((column, colIdx) => (
          <Box key={colIdx} sx={{ minWidth: 180 }}>
            {column.map((t, idx) => (
              <Box
                key={t.transaction_id || idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  fontSize: "0.7rem",
                  lineHeight: 1.4,
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  py: 0.25,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <span style={{ opacity: 0.7 }}>
                    {new Date(t.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      timeZone: "UTC",
                    })}
                  </span>
                  <span
                    style={{
                      maxWidth: 100,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.merchant_name || t.name}
                  </span>
                </Box>
                <span style={{ color: "#4caf50", fontWeight: 500 }}>
                  +{formatCurrency(Math.abs(t.amount))}
                </span>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  // Hover tooltip: the account's last 10 transactions with full meta
  // (date, time, name/description, amount, running balance).
  const renderRecentTransactionsTooltip = (account: PlaidAccount) => {
    const recent = transactions
      .filter((t) => t.account_id === account.account_id)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 10);
    if (recent.length === 0) return "No transactions in the selected period";

    // Running balance working back from the account's current balance.
    const delta = (t: PlaidTransaction) =>
      t.source === "manual" ? t.amount : -t.amount;
    let bal = account.balances?.current ?? account.balances?.available ?? 0;
    const withBalance = recent.map((t) => {
      const row = { t, balance: bal };
      bal -= delta(t);
      return row;
    });

    const fmtTime = (t: PlaidTransaction) => {
      const dt = t.datetime || t.authorized_datetime;
      if (!dt) return "";
      const d = new Date(dt);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    };

    return (
      <Box sx={{ p: 1, minWidth: 340, maxWidth: 460 }}>
        <Typography
          sx={{ fontSize: "0.72rem", fontWeight: 600, mb: 0.5, opacity: 0.9 }}
        >
          Last {withBalance.length} transactions
        </Typography>
        {withBalance.map(({ t, balance }, idx) => {
          const inflow = isDeposit(t);
          return (
            <Box
              key={t.transaction_id || idx}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                columnGap: 1,
                alignItems: "baseline",
                fontSize: "0.7rem",
                lineHeight: 1.35,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                py: 0.35,
              }}
            >
              <Box sx={{ opacity: 0.7, whiteSpace: "nowrap" }}>
                {new Date(t.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "2-digit",
                  timeZone: "UTC",
                })}
                {fmtTime(t) ? (
                  <span style={{ opacity: 0.7 }}> · {fmtTime(t)}</span>
                ) : null}
              </Box>
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.merchant_name || t.name || t.original_description || "—"}
                {t.name && t.merchant_name && t.name !== t.merchant_name ? (
                  <span style={{ opacity: 0.6 }}> · {t.name}</span>
                ) : null}
              </Box>
              <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <span
                  style={{
                    color: inflow ? "#4caf50" : "#ff8a80",
                    fontWeight: 500,
                  }}
                >
                  {inflow ? "+" : "-"}
                  {formatCurrency(Math.abs(t.amount))}
                </span>
                <span style={{ opacity: 0.7, marginLeft: 8 }}>
                  bal {formatCurrency(balance)}
                </span>
              </Box>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: "0.62rem", opacity: 0.6, mt: 0.5 }}>
          Balance is estimated from current balance. Click the row for full
          history.
        </Typography>
      </Box>
    );
  };

  // De-duplicated count of people/groups a SET of accounts is shared with, so
  // an org/institution summary row can show one badge that unions its members.
  const groupShareCount = (accts: PlaidAccount[]): number => {
    const groups = new Set<string>();
    accts.forEach((a) => {
      (shareGroupIdsByAccount[acctId(a)] || []).forEach((g) => groups.add(g));
    });
    return groups.size;
  };

  // Summed Available and Current balances across a SET of accounts, mirroring
  // the per-account dual-balance logic used on each row.
  const groupBalances = (accts: PlaidAccount[]) => {
    let available = 0;
    let current = 0;
    let hasCurrent = false;
    accts.forEach((a) => {
      available += a.balances?.available ?? a.balances?.current ?? 0;
      if (a.balances?.current != null) {
        current += a.balances.current;
        hasCurrent = true;
      }
    });
    return { available, current, hasCurrent };
  };

  // Hover tooltip for a summary row: the most recent transactions across ALL
  // accounts in the group (with the account they belong to). No running
  // balance here — it isn't meaningful across mixed accounts; the per-account
  // row tooltip carries that.
  const renderGroupRecentTransactionsTooltip = (accts: PlaidAccount[]) => {
    const ids = new Set(accts.map((a) => a.account_id));
    const nameById = new Map(
      accts.map((a) => [a.account_id, getAccountDisplayName(a)])
    );
    const recent = transactions
      .filter((t) => ids.has(t.account_id))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 10);
    if (recent.length === 0) return "No transactions in the selected period";

    const fmtTime = (t: PlaidTransaction) => {
      const dt = t.datetime || t.authorized_datetime;
      if (!dt) return "";
      const d = new Date(dt);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    };

    return (
      <Box sx={{ p: 1, minWidth: 340, maxWidth: 480 }}>
        <Typography
          sx={{ fontSize: "0.72rem", fontWeight: 600, mb: 0.5, opacity: 0.9 }}
        >
          Recent activity · {recent.length} transactions
        </Typography>
        {recent.map((t, idx) => {
          const inflow = isDeposit(t);
          return (
            <Box
              key={t.transaction_id || idx}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                columnGap: 1,
                alignItems: "baseline",
                fontSize: "0.7rem",
                lineHeight: 1.35,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                py: 0.35,
              }}
            >
              <Box sx={{ opacity: 0.7, whiteSpace: "nowrap" }}>
                {new Date(t.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "2-digit",
                  timeZone: "UTC",
                })}
                {fmtTime(t) ? (
                  <span style={{ opacity: 0.7 }}> · {fmtTime(t)}</span>
                ) : null}
              </Box>
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.merchant_name || t.name || t.original_description || "—"}
                <span style={{ opacity: 0.6 }}>
                  {" "}
                  · {nameById.get(t.account_id) || "account"}
                </span>
              </Box>
              <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <span
                  style={{
                    color: inflow ? "#4caf50" : "#ff8a80",
                    fontWeight: 500,
                  }}
                >
                  {inflow ? "+" : "-"}
                  {formatCurrency(Math.abs(t.amount))}
                </span>
              </Box>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: "0.62rem", opacity: 0.6, mt: 0.5 }}>
          Expand a bank and hover an account for its running balance, or click a
          row for full history.
        </Typography>
      </Box>
    );
  };

  // Calculate monthly deposits data for the timeline chart
  const monthlyDepositsData = useMemo(() => {
    if (!startDate || !endDate || accounts.length === 0) {
      return null;
    }

    // Generate all months in the selected date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months: string[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);

    while (current <= end) {
      months.push(
        current.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
      );
      current.setMonth(current.getMonth() + 1);
    }

    // Generate unique colors for each account using HSL color space
    const generateColor = (index: number) => {
      // Distribute hues evenly across the color wheel (0-360)
      // Use golden ratio to maximize visual distinction between adjacent colors
      const goldenRatio = 0.618033988749895;
      const hue = ((index * goldenRatio) % 1) * 360;
      const saturation = 70 + (index % 3) * 10; // Vary saturation slightly: 70%, 80%, 90%
      const lightness = 45 + (index % 2) * 10; // Vary lightness: 45%, 55%
      return {
        line: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        bg: `hsla(${hue}, ${saturation}%, ${lightness}%, 0.2)`,
      };
    };

    // Calculate monthly deposits per account
    const datasets = accounts.map((account, idx) => {
      const monthlyData = months.map((monthLabel, monthIdx) => {
        // Determine the month boundaries
        const monthDate = new Date(
          start.getFullYear(),
          start.getMonth() + monthIdx,
          1
        );
        const monthStart = new Date(
          monthDate.getFullYear(),
          monthDate.getMonth(),
          1
        );
        const monthEnd = new Date(
          monthDate.getFullYear(),
          monthDate.getMonth() + 1,
          0
        );

        // Sum deposits for this account in this month
        const deposits = transactions
          .filter((t) => {
            if (t.account_id !== account.account_id || !isDeposit(t)) {
              return false;
            }
            const txDate = new Date(t.date);
            return txDate >= monthStart && txDate <= monthEnd;
          })
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);

        return deposits;
      });

      const color = generateColor(idx);
      const displayName = toProperCase(
        customNames[account.account_id] || account.name
      );

      // Determine account category for filtering
      let accountCategory: "checking" | "savings" | "credit" | "other" =
        "other";
      if (account.type === "credit") {
        accountCategory = "credit";
      } else if (account.subtype === "savings") {
        accountCategory = "savings";
      } else if (
        account.subtype === "checking" ||
        account.type === "depository"
      ) {
        accountCategory = "checking";
      }

      return {
        label: displayName,
        accountId: account.account_id,
        accountCategory,
        data: monthlyData,
        borderColor: color.line,
        backgroundColor: color.bg,
        tension: 0.3,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        hidden: hiddenChartAccounts.has(account.account_id),
      };
    });

    // Add Stripe accounts to the chart
    stripeAccounts.forEach((stripeAccount, idx) => {
      const monthlyData = months.map((monthLabel, monthIdx) => {
        const monthDate = new Date(
          start.getFullYear(),
          start.getMonth() + monthIdx,
          1
        );
        const monthStart = new Date(
          monthDate.getFullYear(),
          monthDate.getMonth(),
          1
        );
        const monthEnd = new Date(
          monthDate.getFullYear(),
          monthDate.getMonth() + 1,
          0
        );

        const deposits = stripeTransactions
          .filter((t) => {
            if (t.account_id !== `stripe_${stripeAccount.stripe_user_id}`)
              return false;
            if (t.status !== "succeeded" || t.refunded) return false;
            const txDate = new Date(t.date);
            return txDate >= monthStart && txDate <= monthEnd;
          })
          .reduce((sum, t) => sum + t.amount, 0);

        return deposits;
      });

      const color = generateColor(accounts.length + idx);
      const stripeAccountId = `stripe_${stripeAccount.stripe_user_id}`;

      datasets.push({
        label: stripeAccount.business_name || "Stripe",
        accountId: stripeAccountId,
        accountCategory: "other" as const,
        data: monthlyData,
        borderColor: color.line,
        backgroundColor: color.bg,
        tension: 0.3,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        hidden: hiddenChartAccounts.has(stripeAccountId),
      });
    });

    return {
      labels: months,
      datasets,
    };
  }, [
    accounts,
    stripeAccounts,
    transactions,
    stripeTransactions,
    startDate,
    endDate,
    customNames,
    hiddenChartAccounts,
    isDeposit,
  ]);

  // Filter datasets based on the selected timeline tab
  const filteredTimelineData = useMemo(() => {
    if (!monthlyDepositsData) return null;

    if (timelineTab === "all") {
      return monthlyDepositsData;
    }

    const filteredDatasets = monthlyDepositsData.datasets.filter(
      (dataset) => dataset.accountCategory === timelineTab
    );

    return {
      labels: monthlyDepositsData.labels,
      datasets: filteredDatasets,
    };
  }, [monthlyDepositsData, timelineTab]);

  // Helper to determine if an account is business or personal
  const getAccountCategory = (
    account: PlaidAccount
  ): "business" | "personal" => {
    // First check holder_category from Plaid
    if (account.holder_category === "business") return "business";
    if (account.holder_category === "personal") return "personal";

    // Fallback: check official_name or name for business keywords
    const nameToCheck = (
      account.official_name ||
      account.name ||
      ""
    ).toLowerCase();
    const businessKeywords = [
      "business",
      "commercial",
      "corp",
      "llc",
      "inc",
      "company",
      "enterprise",
    ];
    if (businessKeywords.some((keyword) => nameToCheck.includes(keyword))) {
      return "business";
    }

    return "personal";
  };

  const visibleAccounts = useMemo(() => {
    if (!sharedOnly) {
      return accounts;
    }
    return accounts.filter((account) => account.is_shared || account.isShared);
  }, [accounts, sharedOnly]);

  const visibleStripeAccounts = useMemo(() => {
    if (!sharedOnly) {
      return stripeAccounts;
    }
    return stripeAccounts.filter(
      (account) =>
        (account as StripeAccount & { is_shared?: boolean; isShared?: boolean })
          .is_shared ||
        (account as StripeAccount & { is_shared?: boolean; isShared?: boolean })
          .isShared
    );
  }, [stripeAccounts, sharedOnly]);

  // Group accounts by institution (bank) with sub-groups for business/personal
  // Build institution-grouped accounts from an arbitrary account list. Used by
  // both the global "Account" tab and the per-organization buckets so that each
  // org renders ONLY its own member accounts (never a shared institution group
  // spanning multiple orgs). Grouping is keyed on institution id, but each
  // group's accounts come exclusively from the passed-in list.
  const buildInstitutionGroups = useCallback(
    (accountList: PlaidAccount[]) => {
      type SubGroup = {
        category: "business" | "personal";
        accounts: PlaidAccount[];
        totalBalance: number;
        totalDeposits: number;
      };

      const groups: {
        institutionId: string;
        institutionName: string;
        logo: string | null;
        primaryColor: string | null;
        subGroups: SubGroup[];
        totalBalance: number;
        totalDeposits: number;
      }[] = [];

      accountList.forEach((account) => {
        const institutionId =
          account.institution?.institution_id || account.item_id;
        const category = getAccountCategory(account);

        let existingGroup = groups.find(
          (g) => g.institutionId === institutionId
        );

        if (!existingGroup) {
          existingGroup = {
            institutionId,
            institutionName: account.institution?.name || "Unknown Bank",
            logo: account.institution?.logo || null,
            primaryColor: account.institution?.primary_color || null,
            subGroups: [],
            totalBalance: 0,
            totalDeposits: 0,
          };
          groups.push(existingGroup);
        }

        // Find or create sub-group for this category
        let subGroup = existingGroup.subGroups.find(
          (sg) => sg.category === category
        );
        if (!subGroup) {
          subGroup = {
            category,
            accounts: [],
            totalBalance: 0,
            totalDeposits: 0,
          };
          existingGroup.subGroups.push(subGroup);
        }

        subGroup.accounts.push(account);
      });

      // Calculate totals for each group and sub-group
      groups.forEach((group) => {
        group.subGroups.forEach((subGroup) => {
          subGroup.totalBalance = subGroup.accounts.reduce(
            (sum, acc) => sum + (acc.balances?.available || 0),
            0
          );
          subGroup.totalDeposits = subGroup.accounts.reduce((sum, acc) => {
            const deposits = transactions
              .filter((t) => t.account_id === acc.account_id && isDeposit(t))
              .reduce((s, t) => s + Math.abs(t.amount), 0);
            return sum + deposits;
          }, 0);
        });

        // Sort sub-groups: Business first, then Personal
        group.subGroups.sort((a, b) => {
          if (a.category === "business" && b.category === "personal") return -1;
          if (a.category === "personal" && b.category === "business") return 1;
          return 0;
        });

        // Sort accounts within each sub-group by deposits (most first)
        group.subGroups.forEach((subGroup) => {
          subGroup.accounts.sort((a, b) => {
            const getAccountDeposits = (acc: PlaidAccount) =>
              transactions
                .filter((t) => t.account_id === acc.account_id && isDeposit(t))
                .reduce((sum, t) => sum + Math.abs(t.amount), 0);
            return getAccountDeposits(b) - getAccountDeposits(a);
          });
        });

        // Calculate group totals
        group.totalBalance = group.subGroups.reduce(
          (sum, sg) => sum + sg.totalBalance,
          0
        );
        group.totalDeposits = group.subGroups.reduce(
          (sum, sg) => sum + sg.totalDeposits,
          0
        );
      });

      // Sort groups by total deposits (most deposits first)
      groups.sort((a, b) => b.totalDeposits - a.totalDeposits);

      return groups;
    },
    [transactions, isDeposit]
  );

  const groupedAccounts = useMemo(
    () => buildInstitutionGroups(visibleAccounts),
    [buildInstitutionGroups, visibleAccounts]
  );

  // Real "Last Imported" timestamps. Combines two sources: Plaid sync
  // (last_successful_update per item) and manual/CSV imports
  // (transaction_import_history per account). We expose a per-account map so
  // institution and organization accordions can roll up the most recent of any
  // of their accounts.
  const plaidTsByItem = useMemo(() => {
    const map: Record<string, string> = {};
    syncStatus.forEach((s) => {
      const ts = s.transactions?.last_successful_update;
      if (ts) map[s.item_id] = ts;
    });
    return map;
  }, [syncStatus]);

  const moreRecentIso = (
    a: string | undefined,
    b: string | undefined
  ): string | undefined => {
    if (!a) return b;
    if (!b) return a;
    return new Date(a) >= new Date(b) ? a : b;
  };

  const lastImportByAccount = useMemo(() => {
    const map: Record<string, string> = {};
    visibleAccounts.forEach((acc) => {
      const ts = moreRecentIso(
        plaidTsByItem[acc.item_id],
        manualImportByAccount[acc.account_id]
      );
      if (ts) map[acc.account_id] = ts;
    });
    return map;
  }, [visibleAccounts, plaidTsByItem, manualImportByAccount]);

  // Most recent import across an arbitrary set of accounts.
  const lastImportForAccounts = useCallback(
    (accts: PlaidAccount[]): string | undefined => {
      let latest: string | undefined;
      accts.forEach((a) => {
        latest = moreRecentIso(latest, lastImportByAccount[a.account_id]);
      });
      return latest;
    },
    [lastImportByAccount]
  );

  // Shared "Last Imported on … [⟳]" line used at org / institution / account
  // levels. itemIds are the Plaid items the refresh button re-syncs.
  // When `accountId` is provided the refresh button scopes to that single
  // account (org/institution callers omit it to refresh their whole set).
  const renderLastImported = (
    timestamp: string | undefined,
    itemIds: string[],
    showRefresh = true,
    accountId?: string
  ) => {
    if (!timestamp) return null;
    const ids = Array.from(new Set(itemIds.filter(Boolean)));
    const isSingleAccount = Boolean(accountId);
    const refreshing = isSingleAccount
      ? refreshingAccounts.has(accountId as string)
      : ids.some((id) => refreshingItems.has(id));
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography variant="caption" color="text.secondary">
          Last Imported on {formatImportedAt(timestamp)}
        </Typography>
        {showRefresh && ids.length > 0 && (
          <Tooltip
            title={isSingleAccount ? "Refresh this account" : "Refresh now"}
            arrow
          >
            <span>
              <IconButton
                component="div"
                role="button"
                size="small"
                disabled={refreshing}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isSingleAccount) {
                    handleRefreshSingleAccount(accountId as string, ids[0]);
                  } else {
                    handleRefreshItems(ids);
                  }
                }}
                sx={{ p: 0.25 }}
              >
                {refreshing ? (
                  <CircularProgress size={12} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Group accounts by organization
  const accountsByOrganization = useMemo(() => {
    type OrgGroup = {
      organization: Organization | null;
      accounts: PlaidAccount[];
      stripeAccounts: StripeAccount[];
      totalBalance: number;
    };

    const orgGroups: OrgGroup[] = [];
    const processedAccountIds = new Set<string>();

    // First, group accounts that have organizations
    organizations.forEach((org) => {
      const orgAccounts = visibleAccounts.filter(
        (acc) => accountOrganizations[acc.account_id] === org.id
      );
      const orgStripeAccounts = visibleStripeAccounts.filter(
        (acc) => accountOrganizations[acc.id] === org.id
      );

      orgAccounts.forEach((acc) => processedAccountIds.add(acc.account_id));
      orgStripeAccounts.forEach((acc) => processedAccountIds.add(acc.id));

      if (orgAccounts.length > 0 || orgStripeAccounts.length > 0) {
        const totalBalance =
          orgAccounts.reduce(
            (sum, acc) =>
              sum + (acc.balances?.available || acc.balances?.current || 0),
            0
          ) +
          orgStripeAccounts.reduce(
            (sum, acc) => sum + (acc.balance?.available || 0),
            0
          );

        orgGroups.push({
          organization: org,
          accounts: orgAccounts,
          stripeAccounts: orgStripeAccounts,
          totalBalance,
        });
      }
    });

    // Add unassigned accounts
    const unassignedAccounts = visibleAccounts.filter(
      (acc) => !processedAccountIds.has(acc.account_id)
    );
    const unassignedStripeAccounts = visibleStripeAccounts.filter(
      (acc) => !processedAccountIds.has(acc.id)
    );

    if (unassignedAccounts.length > 0 || unassignedStripeAccounts.length > 0) {
      const totalBalance =
        unassignedAccounts.reduce(
          (sum, acc) =>
            sum + (acc.balances?.available || acc.balances?.current || 0),
          0
        ) +
        unassignedStripeAccounts.reduce(
          (sum, acc) => sum + (acc.balance?.available || 0),
          0
        );

      orgGroups.push({
        organization: null,
        accounts: unassignedAccounts,
        stripeAccounts: unassignedStripeAccounts,
        totalBalance,
      });
    }

    // Sort: organizations with accounts first (by name), unassigned last
    orgGroups.sort((a, b) => {
      if (!a.organization && b.organization) return 1;
      if (a.organization && !b.organization) return -1;
      if (a.organization && b.organization) {
        return a.organization.name.localeCompare(b.organization.name);
      }
      return 0;
    });

    return orgGroups;
  }, [
    visibleAccounts,
    visibleStripeAccounts,
    organizations,
    accountOrganizations,
  ]);

  // Get unique merchants from transactions viewer data for filter dropdown
  const uniqueMerchants = useMemo(() => {
    const merchants = new Set<string>();
    transactionsViewerData.forEach((txn) => {
      if (txn.merchant_name) {
        merchants.add(txn.merchant_name);
      }
    });
    return Array.from(merchants).sort((a, b) => a.localeCompare(b));
  }, [transactionsViewerData]);

  // Get unique categories from transactions viewer data for filter dropdown
  const uniqueCategories = useMemo(() => {
    const categories = new Set<string>();
    // Add State and Federal as special categories
    categories.add("Federal");
    categories.add("State");
    transactionsViewerData.forEach((txn) => {
      if (Array.isArray(txn.category)) {
        txn.category.forEach((cat) => categories.add(cat));
      } else if (txn.category) {
        categories.add(txn.category);
      }
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [transactionsViewerData]);

  // Filter and sort transactions viewer data
  const filteredSortedTransactions = useMemo(() => {
    let filtered = [...transactionsViewerData];

    // Apply source filter
    if (txnFilterSource) {
      filtered = filtered.filter((txn) => {
        if (txnFilterSource === "plaid") return txn.source !== "manual";
        if (txnFilterSource === "manual") return txn.source === "manual";
        return true;
      });
    }

    // Apply merchant filter
    if (txnFilterMerchant) {
      filtered = filtered.filter(
        (txn) => txn.merchant_name === txnFilterMerchant
      );
    }

    // Apply category filter
    if (txnFilterCategory) {
      filtered = filtered.filter((txn) => {
        // Handle Federal and State as special categories (check name/merchant for government-related)
        if (txnFilterCategory === "Federal") {
          const name = (txn.name || "").toLowerCase();
          const merchant = (txn.merchant_name || "").toLowerCase();
          return (
            name.includes("irs") ||
            name.includes("federal") ||
            name.includes("treasury") ||
            merchant.includes("irs") ||
            merchant.includes("federal") ||
            merchant.includes("treasury")
          );
        }
        if (txnFilterCategory === "State") {
          const name = (txn.name || "").toLowerCase();
          const merchant = (txn.merchant_name || "").toLowerCase();
          return (
            name.includes("state") ||
            name.includes("oregon") ||
            name.includes("dor") ||
            merchant.includes("state") ||
            merchant.includes("oregon") ||
            merchant.includes("dor")
          );
        }
        // Regular category matching
        if (Array.isArray(txn.category)) {
          return txn.category.includes(txnFilterCategory);
        }
        return txn.category === txnFilterCategory;
      });
    }

    // Apply date-range filter (inclusive; blank = unbounded)
    if (txnDateFrom) {
      filtered = filtered.filter((txn) => txn.date >= txnDateFrom);
    }
    if (txnDateTo) {
      filtered = filtered.filter((txn) => txn.date <= txnDateTo);
    }

    // Apply free-text search across name, merchant, category and amount
    const q = txnSearch.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((txn) => {
        const category = Array.isArray(txn.category)
          ? txn.category.join(", ")
          : txn.category || "";
        const haystack = [
          txn.name || "",
          txn.merchant_name || "",
          txn.original_description || "",
          category,
          String(txn.amount),
          Math.abs(txn.amount).toFixed(2),
          txn.date || "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (txnSortColumn) {
        case "date":
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "name":
          comparison = (a.name || "").localeCompare(b.name || "");
          break;
        case "merchant_name":
          comparison = (a.merchant_name || "").localeCompare(
            b.merchant_name || ""
          );
          break;
        case "category":
          const catA = Array.isArray(a.category)
            ? a.category.join(", ")
            : a.category || "";
          const catB = Array.isArray(b.category)
            ? b.category.join(", ")
            : b.category || "";
          comparison = catA.localeCompare(catB);
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "source":
          comparison = (a.source || "plaid").localeCompare(b.source || "plaid");
          break;
      }
      return txnSortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [
    transactionsViewerData,
    txnFilterSource,
    txnFilterMerchant,
    txnFilterCategory,
    txnDateFrom,
    txnDateTo,
    txnSearch,
    txnSortColumn,
    txnSortDirection,
  ]);

  // Running balance per transaction, derived from the account's current balance
  // working backwards through history (newest = current). Approximate: it assumes
  // the loaded set is the account's full ledger. Keyed by transaction_id.
  const runningBalanceByTxn = useMemo(() => {
    const map: Record<string, number> = {};
    const data = transactionsViewerData;
    if (data.length === 0) return map;
    // Newest-first ordering, independent of the table's chosen sort.
    const chrono = [...data].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    // Effect of a txn on the balance: plaid amount>0 is an outflow (balance fell),
    // manual amount>0 is an inflow (balance rose).
    const delta = (t: PlaidTransaction) =>
      t.source === "manual" ? t.amount : -t.amount;
    const acct = accounts.find((a) => a.account_id === chrono[0].account_id);
    let balance = acct?.balances?.current ?? acct?.balances?.available ?? 0;
    for (const t of chrono) {
      map[t.transaction_id] = balance;
      // Step back before this txn to get the prior balance for the next (older) row.
      balance -= delta(t);
    }
    return map;
  }, [transactionsViewerData, accounts]);

  // Reset to the first page whenever the filtered result set changes.
  useEffect(() => {
    setTxnPage(0);
  }, [
    txnFilterSource,
    txnFilterMerchant,
    txnFilterCategory,
    txnDateFrom,
    txnDateTo,
    txnSearch,
    txnRowsPerPage,
  ]);

  // The current page of transactions to render.
  const paginatedTransactions = useMemo(() => {
    const start = txnPage * txnRowsPerPage;
    return filteredSortedTransactions.slice(start, start + txnRowsPerPage);
  }, [filteredSortedTransactions, txnPage, txnRowsPerPage]);

  const txnPageCount = Math.max(
    1,
    Math.ceil(filteredSortedTransactions.length / txnRowsPerPage)
  );

  // Load how many people each account is shared with, for the share badge.
  useEffect(() => {
    const ids = accounts.map((a) => acctId(a)).filter(Boolean);
    if (ids.length === 0) {
      setShareCountsByAccount({});
      setShareGroupIdsByAccount({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const shares = await shareAPI.getSharesForRecord("account", id);
            // Distinct groups the account is shared with.
            const groupIds = Array.from(new Set(shares.map((s) => s.groupId)));
            return [id, groupIds] as const;
          } catch {
            return [id, [] as string[]] as const;
          }
        })
      );
      if (!cancelled) {
        setShareCountsByAccount(
          Object.fromEntries(entries.map(([id, g]) => [id, g.length]))
        );
        setShareGroupIdsByAccount(Object.fromEntries(entries));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.map((a) => acctId(a)).join(",")]);

  // Handle sort click on table header
  const handleTxnSort = (column: SortColumn) => {
    if (txnSortColumn === column) {
      setTxnSortDirection(txnSortDirection === "asc" ? "desc" : "asc");
    } else {
      setTxnSortColumn(column);
      setTxnSortDirection("desc");
    }
  };

  // Reset filters when dialog opens. Defaults the date range to the past 30
  // days so the viewer opens on recent history; the user can widen or clear it.
  const resetTxnFilters = (dateRange: "last30" | "all" = "last30") => {
    setTxnSortColumn("date");
    setTxnSortDirection("desc");
    setTxnFilterSource("");
    setTxnFilterMerchant("");
    setTxnFilterCategory("");
    setTxnSearch("");
    setTxnPage(0);
    setTxnJumpPage("");
    if (dateRange === "last30") {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 30);
      setTxnDateFrom(from.toISOString().split("T")[0]);
      setTxnDateTo(to.toISOString().split("T")[0]);
    } else {
      setTxnDateFrom("");
      setTxnDateTo("");
    }
  };

  // Calculate deposits for a Stripe account
  const getStripeAccountDeposits = (stripeAccountId: string): number => {
    return stripeTransactions
      .filter(
        (t) =>
          t.account_id === `stripe_${stripeAccountId}` &&
          t.status === "succeeded" &&
          !t.refunded
      )
      .reduce((sum, t) => sum + t.amount, 0);
  };

  // Get oldest Stripe transaction date for an account
  const getOldestStripeRecordDate = (
    stripeAccountId: string
  ): string | null => {
    const accountTransactions = stripeTransactions.filter(
      (t) => t.account_id === `stripe_${stripeAccountId}`
    );
    if (accountTransactions.length === 0) return null;

    const oldestDate = accountTransactions.reduce((oldest, t) => {
      return t.date < oldest ? t.date : oldest;
    }, accountTransactions[0].date);

    return oldestDate;
  };

  const getCombinedOldestDate = (
    plaidDate?: string | null,
    manualDate?: string | null
  ): string | null => {
    if (!plaidDate && !manualDate) return null;
    if (!plaidDate) return manualDate || null;
    if (!manualDate) return plaidDate || null;
    return new Date(plaidDate) <= new Date(manualDate) ? plaidDate : manualDate;
  };

  // Check if account is PayPal and get username/email
  const isPayPalAccount = (account: PlaidAccount): boolean => {
    return (
      account.institution?.name?.toLowerCase().includes("paypal") ||
      account.name?.toLowerCase().includes("paypal") ||
      account.official_name?.toLowerCase().includes("paypal") ||
      false
    );
  };

  // Get PayPal username/email from account data
  const getPayPalUsername = (account: PlaidAccount): string | null => {
    if (!isPayPalAccount(account)) return null;
    // PayPal accounts often have email in official_name or name
    if (account.official_name && account.official_name.includes("@")) {
      return account.official_name;
    }
    if (account.name && account.name.includes("@")) {
      return account.name;
    }
    // Don't return generic names like "PayPal"
    return null;
  };

  // Get a display ID for PayPal accounts (email or account reference)
  const getPayPalDisplayId = (account: PlaidAccount): string => {
    const email = getPayPalUsername(account);
    if (email) return email;
    // Use the last part of the Plaid account_id as a reference
    // Format: PP-XXXX (last 8 chars of account_id)
    const shortId = account.account_id.slice(-8).toUpperCase();
    return `Ref: ${shortId}`;
  };

  // Connect Stripe account
  const handleConnectStripe = async () => {
    try {
      setConnectingStripe(true);
      await stripeConnectService.initiateConnect();
    } catch (err) {
      console.error("Error initiating Stripe connect:", err);
      setSnackbar({
        open: true,
        message: "Failed to initiate Stripe connection",
        severity: "error",
      });
      setConnectingStripe(false);
    }
  };

  // Disconnect Stripe account
  const handleDisconnectStripe = async (connectionId: string) => {
    try {
      await stripeConnectService.disconnect(connectionId);
      setSnackbar({
        open: true,
        message: "Stripe account disconnected",
        severity: "success",
      });
      loadStripeAccounts();
    } catch (err) {
      console.error("Error disconnecting Stripe:", err);
      setSnackbar({
        open: true,
        message: "Failed to disconnect Stripe account",
        severity: "error",
      });
    }
  };

  // Delete Plaid connection
  const handleDeletePlaidConnection = async (itemId: string) => {
    try {
      await plaidService.deleteConnection(itemId);
      setSnackbar({
        open: true,
        message: "Bank account disconnected",
        severity: "success",
      });
      loadAccounts();
      loadTransactions();
    } catch (err) {
      console.error("Error deleting Plaid connection:", err);
      setSnackbar({
        open: true,
        message: "Failed to disconnect bank account",
        severity: "error",
      });
    }
  };

  const handleOpenDeleteAllDialog = () => {
    console.debug("[DEBUG] Opening delete all accounts dialog", {
      plaidAccounts: accounts.length,
      stripeAccounts: stripeAccounts.length,
    });
    setDeleteAllDialogOpen(true);
  };

  const handleCloseDeleteAllDialog = () => {
    if (deletingAllAccounts) return;
    setDeleteAllDialogOpen(false);
    setDeleteAllPassword("");
  };

  const handleDeleteAllAccounts = async () => {
    if (!deleteAllPassword.trim()) return;

    try {
      setDeletingAllAccounts(true);
      console.debug("[DEBUG] Deleting all accounts", {
        plaidAccounts: accounts.length,
        stripeAccounts: stripeAccounts.length,
      });
      const result = await plaidService.deleteAllAccounts(deleteAllPassword);

      setAccounts([]);
      setStripeAccounts([]);
      setTransactions([]);
      setStripeTransactions([]);
      setTotalDeposits(0);
      setSyncStatus([]);
      setShowSyncStatus(false);
      setManualTransactionCounts({});
      setAccountTransactionStats({});
      setCustomNames({});
      setAccountOrganizations({});
      setHiddenChartAccounts(new Set());
      setDeleteAllDialogOpen(false);
      setDeleteAllPassword("");

      setSnackbar({
        open: true,
        message:
          result.summary &&
          (result.summary.plaidItemsDeleted > 0 ||
            result.summary.stripeConnectionsDeleted > 0)
            ? `Deleted ${result.summary.plaidItemsDeleted} bank connection${result.summary.plaidItemsDeleted === 1 ? "" : "s"} and ${result.summary.stripeConnectionsDeleted} Stripe connection${result.summary.stripeConnectionsDeleted === 1 ? "" : "s"}`
            : "All accounts deleted",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting all accounts:", err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : "Failed to delete all accounts",
        severity: "error",
      });
    } finally {
      setDeletingAllAccounts(false);
    }
  };

  // Load sync status for all connections
  const handleCheckSyncStatus = async () => {
    try {
      setLoadingSyncStatus(true);
      const response = await plaidService.getSyncStatus();
      setSyncStatus(response.items);
      setManualTransactionCounts(response.manualTransactionCounts);
      setAccountTransactionStats(response.accountTransactionStats || {});
      setShowSyncStatus(true);
    } catch (err) {
      console.error("Error loading sync status:", err);
      setSnackbar({
        open: true,
        message: "Failed to load sync status",
        severity: "error",
      });
    } finally {
      setLoadingSyncStatus(false);
    }
  };

  // Force refresh transactions/balance for an item
  const handleRefreshAccount = async (itemId: string) => {
    await handleRefreshItems([itemId]);
  };

  // Refresh a SINGLE account (task 956adfb3). The spinner is scoped to this
  // account_id only — sibling accounts on the same institution do not spin.
  //
  // NOTE / BACKEND GAP: Plaid's transactionsRefresh API operates per
  // access_token (i.e. per Plaid *item*), not per individual account. The only
  // existing endpoint is POST /api/plaid/refresh/:itemId. So although the UI
  // spinner is isolated to this one account, the underlying network refresh
  // still asks Plaid to refresh that account's item. A truly account-only
  // refresh would require a new backend endpoint (not available / not editable
  // here). This is the closest correct behavior with current endpoints.
  const handleRefreshSingleAccount = async (
    accountId: string,
    itemId: string
  ) => {
    if (!accountId || !itemId) return;
    console.debug("[AccountsPage][RefreshSingleAccount] start", {
      accountId,
      itemId,
    });
    setRefreshingAccounts((prev) => {
      const next = new Set(prev);
      next.add(accountId);
      return next;
    });
    try {
      await plaidService.refreshTransactions(itemId);
      setSnackbar({
        open: true,
        message: "Refresh triggered for this account. Reloading…",
        severity: "success",
      });
      console.debug("[AccountsPage][RefreshSingleAccount] refresh requested", {
        accountId,
        itemId,
      });
      setTimeout(async () => {
        await loadAccounts();
        await loadTransactions();
        await loadSyncStatus();
        await loadManualImportTimestamps();
        console.debug("[AccountsPage][RefreshSingleAccount] reload complete", {
          accountId,
        });
      }, 3000);
    } catch (err) {
      console.error(
        "[AccountsPage][RefreshSingleAccount] failed",
        accountId,
        err
      );
      setSnackbar({
        open: true,
        message: "Failed to refresh this account",
        severity: "error",
      });
    } finally {
      setRefreshingAccounts((prev) => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  // Refresh one or more Plaid items (used by per-account, per-institution,
  // per-organization, and "Refresh All" controls). Shows a spinner on each
  // affected item until its sync request completes, then reloads.
  const handleRefreshItems = async (
    itemIds: string[],
    opts?: { silent?: boolean }
  ) => {
    const unique = Array.from(new Set(itemIds.filter(Boolean)));
    if (unique.length === 0) return;
    const silent = opts?.silent === true;
    setRefreshingItems((prev) => {
      const next = new Set(prev);
      unique.forEach((id) => next.add(id));
      return next;
    });
    try {
      await Promise.all(
        unique.map((id) => plaidService.refreshTransactions(id))
      );
      if (!silent) {
        setSnackbar({
          open: true,
          message:
            unique.length === 1
              ? "Refresh triggered. Reloading accounts..."
              : `Refreshing ${unique.length} connections. Reloading accounts...`,
          severity: "success",
        });
      }
      // Reload accounts, transactions, sync status, and import timestamps.
      setTimeout(async () => {
        await loadAccounts();
        await loadTransactions();
        await loadSyncStatus();
        await loadManualImportTimestamps();
      }, 3000);
    } catch (err) {
      console.error("Error refreshing accounts:", err);
      if (!silent) {
        setSnackbar({
          open: true,
          message: "Failed to trigger refresh",
          severity: "error",
        });
      }
    } finally {
      setRefreshingItems((prev) => {
        const next = new Set(prev);
        unique.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  // Auto-refresh all connected items once per login (in the background), so a
  // freshly logged-in user sees up-to-date balances/transactions without
  // manually refreshing. Guarded per session to avoid refreshing on every
  // navigation (Plaid bills/rate-limits per refresh).
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (autoRefreshedRef.current) return;
    if (syncStatus.length === 0) return;
    const KEY = "accounts_auto_refreshed_session";
    if (sessionStorage.getItem(KEY)) {
      autoRefreshedRef.current = true;
      return;
    }
    const ids = syncStatus.map((s) => s.item_id).filter(Boolean);
    if (ids.length === 0) return;
    autoRefreshedRef.current = true;
    sessionStorage.setItem(KEY, "1");
    console.log(
      "[AccountsPage] Auto-refreshing all connections on login",
      ids.length
    );
    void handleRefreshItems(ids, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncStatus]);

  // Open edit name dialog
  const handleEditName = (account: PlaidAccount) => {
    setEditingAccount(account);
    setEditNameValue(customNames[account.account_id] || "");
  };

  // Save custom name
  const handleSaveName = async () => {
    if (!editingAccount) return;

    try {
      setSavingName(true);
      if (editNameValue.trim()) {
        await plaidService.setCustomName(
          editingAccount.account_id,
          editNameValue.trim()
        );
        setCustomNames((prev) => ({
          ...prev,
          [editingAccount.account_id]: editNameValue.trim(),
        }));
      } else {
        await plaidService.deleteCustomName(editingAccount.account_id);
        setCustomNames((prev) => {
          const next = { ...prev };
          delete next[editingAccount.account_id];
          return next;
        });
      }
      setSnackbar({
        open: true,
        message: "Account name updated",
        severity: "success",
      });
      setEditingAccount(null);
    } catch (err) {
      console.error("Error saving custom name:", err);
      setSnackbar({
        open: true,
        message: "Failed to save account name",
        severity: "error",
      });
    } finally {
      setSavingName(false);
    }
  };

  // Organization handlers
  const handleOpenOrgDialog = (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      setOrgName(org.name);
      setOrgDescription(org.description || "");
      setOrgColor(org.color);
    } else {
      setEditingOrg(null);
      setOrgName("");
      setOrgDescription("");
      setOrgColor("#1976d2");
    }
    setOrgDialogOpen(true);
  };

  const handleCloseOrgDialog = () => {
    setOrgDialogOpen(false);
    setEditingOrg(null);
    setOrgName("");
    setOrgDescription("");
    setOrgColor("#1976d2");
  };

  const handleSaveOrg = async () => {
    if (!orgName.trim()) return;

    try {
      setSavingOrg(true);
      if (editingOrg) {
        const updated = await organizationService.update(editingOrg.id, {
          name: orgName.trim(),
          description: orgDescription.trim() || undefined,
          color: orgColor,
        });
        setOrganizations((prev) =>
          prev.map((o) => (o.id === updated.id ? updated : o))
        );
        setSnackbar({
          open: true,
          message: "Organization updated",
          severity: "success",
        });
      } else {
        const created = await organizationService.create({
          name: orgName.trim(),
          description: orgDescription.trim() || undefined,
          color: orgColor,
        });
        setOrganizations((prev) =>
          [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
        );
        setSnackbar({
          open: true,
          message: "Organization created",
          severity: "success",
        });
      }
      handleCloseOrgDialog();
    } catch (err) {
      console.error("Error saving organization:", err);
      setSnackbar({
        open: true,
        message: "Failed to save organization",
        severity: "error",
      });
    } finally {
      setSavingOrg(false);
    }
  };

  const handleDeleteOrg = async (orgId: string) => {
    try {
      await organizationService.delete(orgId);
      setOrganizations((prev) => prev.filter((o) => o.id !== orgId));
      // Clear account mappings for this org
      setAccountOrganizations((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((accountId) => {
          if (next[accountId] === orgId) {
            delete next[accountId];
          }
        });
        return next;
      });
      setSnackbar({
        open: true,
        message: "Organization deleted",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting organization:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete organization",
        severity: "error",
      });
    }
  };

  const handleAssignOrg = async (accountId: string, orgId: string | null) => {
    try {
      await organizationService.assignToAccount(accountId, orgId);
      setAccountOrganizations((prev) => {
        const next = { ...prev };
        if (orgId) {
          next[accountId] = orgId;
        } else {
          delete next[accountId];
        }
        return next;
      });
    } catch (err) {
      console.error("Error assigning organization:", err);
      setSnackbar({
        open: true,
        message: "Failed to assign organization",
        severity: "error",
      });
    }
  };

  // Get display name for account (custom name or default)
  const getAccountDisplayName = (account: PlaidAccount): string => {
    return customNames[account.account_id] || account.name;
  };

  // Parse CSV file
  // Delegates to the shared parser (app/src/utils/parseTransactionsCsv.ts) so the
  // browser upload and the local-agent import normalize CSVs identically.
  const parseCSV = (text: string): CSVTransaction[] =>
    parseTransactionsCsv(text) as CSVTransaction[];

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFile(file);

    try {
      const text = await file.text();
      const transactions = parseCSV(text);
      setImportPreview(transactions.slice(0, 10)); // Preview first 10
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to parse CSV",
        severity: "error",
      });
    }
  };

  const handleOpenImportHistory = async (account: PlaidAccount) => {
    try {
      setImportHistoryAccount(account);
      setLoadingImportHistory(true);
      const history = await plaidService.getImportHistory(account.account_id);
      setImportHistory(history);
    } catch (err) {
      console.error("Error loading import history:", err);
      setSnackbar({
        open: true,
        message: "Failed to load import history",
        severity: "error",
      });
    } finally {
      setLoadingImportHistory(false);
    }
  };

  // Open transactions viewer modal
  const handleOpenTransactionsViewer = async (
    account: PlaidAccount,
    source: "plaid" | "manual" | "all",
    scrollToOldest?: boolean
  ) => {
    try {
      setTransactionsViewerLoading(true);
      setTransactionsViewerOpen(true);
      setHighlightTransactionDate(null);
      // Jumping to the oldest transaction needs the full history in view, so
      // skip the default 30-day window in that case.
      resetTxnFilters(scrollToOldest ? "all" : "last30");

      const accountName =
        customNames[account.account_id] || account.name || "Account";
      const sourceLabel =
        source === "plaid" ? "Plaid" : source === "manual" ? "Manual" : "All";
      setTransactionsViewerTitle(
        `${sourceLabel} Transactions - ${accountName}`
      );

      // Fetch all transactions for the account (use wide date range)
      const allTxns = await plaidService.getTransactions(
        "1900-01-01",
        new Date().toISOString().split("T")[0]
      );

      // Filter by account and source
      let filtered = allTxns.filter(
        (txn) => txn.account_id === account.account_id
      );

      if (source === "plaid") {
        filtered = filtered.filter((txn) => txn.source !== "manual");
      } else if (source === "manual") {
        filtered = filtered.filter((txn) => txn.source === "manual");
      }

      // Sort by date descending (newest first)
      filtered.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setTransactionsViewerData(filtered);

      // If scrollToOldest, find the oldest transaction date
      if (scrollToOldest && filtered.length > 0) {
        const oldest = filtered[filtered.length - 1];
        setHighlightTransactionDate(oldest.date);
      }
    } catch (err) {
      console.error("Error loading transactions:", err);
      setSnackbar({
        open: true,
        message: "Failed to load transactions",
        severity: "error",
      });
      setTransactionsViewerOpen(false);
    } finally {
      setTransactionsViewerLoading(false);
    }
  };

  // Import transactions
  const handleImportTransactions = async () => {
    if (!importingAccount || !importFile) return;

    try {
      setImporting(true);
      const text = await importFile.text();
      const transactions = parseCSV(text);

      const result = await plaidService.importTransactions(
        importingAccount.account_id,
        transactions
      );

      setSnackbar({
        open: true,
        message: result.message,
        severity: "success",
      });

      setImportingAccount(null);
      setImportFile(null);
      setImportPreview([]);

      // Reload transactions
      loadTransactions();
    } catch (err) {
      console.error("Error importing transactions:", err);
      setSnackbar({
        open: true,
        message: "Failed to import transactions",
        severity: "error",
      });
    } finally {
      setImporting(false);
    }
  };

  // Trigger a local-agent auto-import for a manual account: enqueue a job, then
  // poll until the user's connector fulfills it (or it times out). If no agent is
  // connected the job simply stays queued — we surface that via the status text.
  const handleAgentImport = async (account: PlaidAccount) => {
    if (agentJobAccountId) return; // one at a time
    try {
      setAgentJobAccountId(account.account_id);
      setAgentJobStatus("Queuing…");
      console.log("[DEBUG] handleAgentImport enqueue", account.account_id);

      const job = await agentService.enqueueJob({
        type: "cashapp_import",
        account_id: account.account_id,
      });

      setSnackbar({
        open: true,
        message:
          "Auto-import requested. Make sure your local agent connector is running (Settings → Connect a local agent).",
        severity: "success",
      });

      const finished = await agentService.waitForJob(job.id, {
        onUpdate: (j) => setAgentJobStatus(j.progress || j.status),
      });

      if (finished.status === "completed") {
        setSnackbar({
          open: true,
          message: finished.result?.message || "Agent import complete",
          severity: "success",
        });
        loadTransactions();
      } else {
        setSnackbar({
          open: true,
          message: `Agent import failed: ${finished.error || "unknown error"}`,
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Error during agent import:", err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? `Agent import error: ${err.message}`
            : "Agent import error",
        severity: "error",
      });
    } finally {
      setAgentJobAccountId(null);
      setAgentJobStatus("");
    }
  };

  // Render a single institution (bank/provider) accordion group. Shared by
  // both the Organization view (filtered per-org) and the Account view (flat,
  // grouped by institution). Param type mirrors groupedAccounts elements.
  const renderInstitutionGroup = (group: (typeof groupedAccounts)[number]) => {
    const groupAccounts = group.subGroups.flatMap((sg) => sg.accounts);
    const groupLastImport = lastImportForAccounts(groupAccounts);
    const groupItemIds = Array.from(
      new Set(groupAccounts.map((a) => a.item_id))
    );
    // The Plaid item id backing this institution (manual accounts have no real
    // Plaid item, so they're excluded). Used by the "Edit Plaid connection"
    // button to launch account-selection update mode for the right item.
    const editablePlaidItemId =
      groupAccounts.find((a) => a.source !== "manual" && Boolean(a.item_id))
        ?.item_id || null;
    return (
      <Accordion
        key={group.institutionId}
        expanded={expandedAccordions.has(group.institutionId)}
        onChange={(_, isExpanded) => {
          setExpandedAccordions((prev) => {
            const next = new Set(prev);
            if (isExpanded) {
              next.add(group.institutionId);
            } else {
              next.delete(group.institutionId);
            }
            return next;
          });
        }}
        sx={{
          mb: 1,
          "&:before": { display: "none" },
          boxShadow: 1,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 1.5,
            minHeight: 56,
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",
              my: 0.75,
            },
            "&.Mui-expanded": { minHeight: 56 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {group.logo ? (
              <Avatar
                src={`data:image/png;base64,${group.logo}`}
                alt={group.institutionName}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: group.primaryColor || "grey.200",
                }}
              >
                {group.institutionName.charAt(0)}
              </Avatar>
            ) : (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: group.primaryColor || "primary.main",
                }}
              >
                <AccountBalanceIcon sx={{ fontSize: 18 }} />
              </Avatar>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Tooltip
                  title={renderGroupRecentTransactionsTooltip(groupAccounts)}
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "rgba(33, 33, 33, 0.95)",
                        maxWidth: "none",
                        "& .MuiTooltip-arrow": {
                          color: "rgba(33, 33, 33, 0.95)",
                        },
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="medium"
                    sx={{
                      textDecoration: "underline dotted",
                      textUnderlineOffset: "3px",
                    }}
                  >
                    {group.institutionName}
                  </Typography>
                </Tooltip>
                <Chip
                  label={`${group.subGroups.reduce(
                    (sum, sg) => sum + sg.accounts.length,
                    0
                  )} account${
                    group.subGroups.reduce(
                      (sum, sg) => sum + sg.accounts.length,
                      0
                    ) > 1
                      ? "s"
                      : ""
                  }`}
                  size="small"
                  sx={{ height: 22, fontSize: "0.75rem" }}
                />
              </Box>
              {renderLastImported(groupLastImport, groupItemIds, false)}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mr: 2,
            }}
          >
            <Box sx={{ textAlign: "right" }}>
              {loading ? (
                <CircularProgress
                  size={16}
                  sx={{ display: "block", mx: "auto" }}
                />
              ) : (
                (() => {
                  const gb = groupBalances(groupAccounts);
                  return (
                    <>
                      <Typography
                        variant="body1"
                        color="primary"
                        fontWeight="medium"
                      >
                        {formatCurrency(gb.available)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Available Balance
                      </Typography>
                      {gb.hasCurrent && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.25 }}
                        >
                          {formatCurrency(gb.current)} current
                        </Typography>
                      )}
                    </>
                  );
                })()
              )}
            </Box>
            <Box sx={{ textAlign: "right" }}>
              {loadingTransactions ? (
                <CircularProgress
                  size={16}
                  sx={{ display: "block", mx: "auto" }}
                />
              ) : (
                <Typography
                  variant="body1"
                  color="success.main"
                  fontWeight="medium"
                >
                  +{formatCurrency(group.totalDeposits)}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Period Deposits
              </Typography>
            </Box>
            <Tooltip
              title={`Shared with ${groupShareCount(groupAccounts)} ${
                groupShareCount(groupAccounts) === 1
                  ? "person/group"
                  : "people/groups"
              } — manage sharing for this bank`}
            >
              <IconButton
                component="div"
                role="button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openManageShare(
                    group.institutionName,
                    groupAccounts.map(acctId)
                  );
                }}
              >
                <Badge
                  badgeContent={groupShareCount(groupAccounts)}
                  showZero
                  color="primary"
                  overlap="circular"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.55rem",
                      height: 14,
                      minWidth: 14,
                    },
                  }}
                >
                  <ShareIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh all accounts in this bank">
              <span>
                <IconButton
                  component="div"
                  role="button"
                  size="small"
                  disabled={groupItemIds.some((id) => refreshingItems.has(id))}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefreshItems(groupItemIds);
                  }}
                >
                  {groupItemIds.some((id) => refreshingItems.has(id)) ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {editablePlaidItemId && (
              <Tooltip title="Edit Plaid connection (add or enable missing accounts)">
                <span>
                  <IconButton
                    component="div"
                    role="button"
                    size="small"
                    disabled={editingConnectionItemId === editablePlaidItemId}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditConnection(editablePlaidItemId);
                    }}
                  >
                    {editingConnectionItemId === editablePlaidItemId ? (
                      <CircularProgress size={18} />
                    ) : (
                      <LinkIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
          {/* Sub-groups: Business and Personal */}
          {group.subGroups.map((subGroup) => (
            <Box
              key={subGroup.category}
              sx={{ mb: 1, "&:last-child": { mb: 0 } }}
            >
              {/* Sub-group header - only show if there are multiple sub-groups */}
              {group.subGroups.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    ml: 1,
                    pb: 0.5,
                    borderBottom: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={
                        subGroup.category === "business"
                          ? "Business"
                          : "Personal"
                      }
                      size="small"
                      color={
                        subGroup.category === "business" ? "primary" : "default"
                      }
                      sx={{ fontWeight: "medium" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {subGroup.accounts.length} account
                      {subGroup.accounts.length > 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Typography variant="body2" color="primary">
                        {formatCurrency(subGroup.totalBalance)}
                      </Typography>
                    )}
                    {loadingTransactions ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Typography variant="body2" color="success.main">
                        +{formatCurrency(subGroup.totalDeposits)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              <List disablePadding>
                {subGroup.accounts.map((account) => (
                  <ListItem
                    key={account.account_id}
                    onClick={(e) => {
                      // Ignore clicks that originate from interactive controls
                      // inside the row (buttons, selects, chips, inputs, links).
                      const target = e.target as HTMLElement;
                      if (
                        target.closest(
                          'button, a, input, select, [role="button"], .MuiSelect-root, .MuiChip-root, .MuiBadge-root'
                        )
                      ) {
                        return;
                      }
                      handleOpenTransactionsViewer(account, "all");
                    }}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 0.75,
                      ml: group.subGroups.length > 1 ? 2 : 1,
                      mr: 1,
                      py: 0.75,
                      px: 1.5,
                      "&:last-child": { mb: 0 },
                      overflow: "hidden",
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <ListItemText
                      sx={{ flex: 1, overflow: "hidden" }}
                      primaryTypographyProps={{
                        component: "div",
                      }}
                      secondaryTypographyProps={{
                        component: "div",
                      }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Tooltip
                            title={renderRecentTransactionsTooltip(account)}
                            arrow
                            placement="right"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "rgba(33, 33, 33, 0.95)",
                                  maxWidth: "none",
                                  "& .MuiTooltip-arrow": {
                                    color: "rgba(33, 33, 33, 0.95)",
                                  },
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="medium"
                              sx={{
                                fontSize: "1rem",
                                textDecoration: "underline dotted",
                                textUnderlineOffset: "3px",
                              }}
                            >
                              {getAccountDisplayName(account)}
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Edit account name">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditName(account);
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={`Shared with ${
                              shareCountsByAccount[acctId(account)] || 0
                            } ${
                              (shareCountsByAccount[acctId(account)] || 0) === 1
                                ? "person/group"
                                : "people/groups"
                            } — manage sharing`}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openManageShare(
                                  getAccountDisplayName(account),
                                  [acctId(account)]
                                );
                              }}
                            >
                              <Badge
                                badgeContent={
                                  shareCountsByAccount[acctId(account)] || 0
                                }
                                showZero
                                color="primary"
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: "0.55rem",
                                    height: 14,
                                    minWidth: 14,
                                  },
                                }}
                              >
                                <ShareIcon sx={{ fontSize: 16 }} />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                          {customNames[account.account_id] && (
                            <Chip
                              label="Custom"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                              }}
                            />
                          )}
                          {/* Organization selector */}
                          <FormControl
                            size="small"
                            sx={{
                              minWidth: 110,
                              maxWidth: 320,
                            }}
                          >
                            <Select
                              value={
                                accountOrganizations[account.account_id] || ""
                              }
                              // Let the control grow to fit the
                              // selected org name instead of
                              // clipping it.
                              autoWidth
                              onChange={(e) => {
                                if (e.target.value === "__add_new__") {
                                  handleOpenOrgDialog();
                                } else {
                                  handleAssignOrg(
                                    account.account_id,
                                    e.target.value || null
                                  );
                                }
                              }}
                              displayEmpty
                              sx={{
                                height: 26,
                                fontSize: "0.7rem",
                                "& .MuiSelect-select": {
                                  py: 0.25,
                                  px: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                },
                              }}
                            >
                              <MenuItem value="">
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  No Organization
                                </Typography>
                              </MenuItem>
                              {organizations.map((org) => (
                                <MenuItem key={org.id} value={org.id}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      bgcolor: org.color,
                                      mr: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography variant="caption">
                                    {org.name}
                                  </Typography>
                                </MenuItem>
                              ))}
                              <Divider sx={{ my: 0.5 }} />
                              <MenuItem value="__add_new__">
                                <AddIcon
                                  sx={{
                                    fontSize: 14,
                                    mr: 1,
                                    color: "primary.main",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  color="primary.main"
                                  fontWeight="medium"
                                >
                                  Add New...
                                </Typography>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                          >
                            {getAccountTypeDisplay(
                              account.type,
                              account.subtype
                            )}
                            {/* Show account mask for non-PayPal accounts */}
                            {!isPayPalAccount(account) &&
                              account.mask &&
                              ` • ••••${account.mask}`}
                          </Typography>
                          {/* Show PayPal ID prominently */}
                          {isPayPalAccount(account) && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                mt: 0.5,
                              }}
                            >
                              PayPal: {getPayPalDisplayId(account)}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              mt: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {account.balances?.current != null && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Current:{" "}
                                {formatCurrency(account.balances.current)}
                              </Typography>
                            )}
                            {account.balances?.limit != null && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Limit: {formatCurrency(account.balances.limit)}
                              </Typography>
                            )}
                          </Box>
                          {/* Sync Status Info */}
                          {loadingSyncStatus ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 1,
                              }}
                            >
                              <CircularProgress size={14} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Loading sync status...
                              </Typography>
                            </Box>
                          ) : accountTransactionStats[account.account_id] ||
                            manualTransactionCounts[account.account_id] ? (
                            <Box sx={{ mt: 1 }}>
                              {(() => {
                                const plaidData =
                                  accountTransactionStats[account.account_id];
                                const plaidCount = plaidData?.total_count || 0;
                                const manualData =
                                  manualTransactionCounts[account.account_id];
                                const manualCount = manualData?.count || 0;
                                const totalCount = plaidCount + manualCount;
                                const oldestDateStr = getCombinedOldestDate(
                                  plaidData?.oldest_date,
                                  manualData?.oldest_date
                                );

                                return (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <SyncIcon sx={{ fontSize: 14 }} />
                                    <strong>
                                      {totalCount.toLocaleString()}
                                    </strong>{" "}
                                    total
                                    {plaidCount > 0 && (
                                      <Chip
                                        label={`${plaidCount.toLocaleString()} Plaid`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        clickable
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenTransactionsViewer(
                                            account,
                                            "plaid"
                                          );
                                        }}
                                        sx={{
                                          height: 18,
                                          fontSize: "0.65rem",
                                          ml: 0.5,
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                    {manualCount > 0 && (
                                      <Chip
                                        label={`${manualCount.toLocaleString()} Manual`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        clickable
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenTransactionsViewer(
                                            account,
                                            "manual"
                                          );
                                        }}
                                        sx={{
                                          height: 18,
                                          fontSize: "0.65rem",
                                          ml: 0.5,
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                    {oldestDateStr && (
                                      <Chip
                                        label={`Since ${new Date(oldestDateStr).toLocaleDateString("en-US", { timeZone: "UTC" })}`}
                                        size="small"
                                        color={(() => {
                                          const oldestDate = new Date(
                                            oldestDateStr
                                          );
                                          const selectedStart = new Date(
                                            startDate
                                          );
                                          return oldestDate.getFullYear() ===
                                            selectedStart.getFullYear() &&
                                            oldestDate.getMonth() ===
                                              selectedStart.getMonth()
                                            ? "default"
                                            : "error";
                                        })()}
                                        clickable
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenTransactionsViewer(
                                            account,
                                            "all",
                                            true
                                          );
                                        }}
                                        sx={{
                                          height: 18,
                                          fontSize: "0.65rem",
                                          ml: 0.5,
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                  </Typography>
                                );
                              })()}
                            </Box>
                          ) : null}
                          <Box sx={{ mt: 0.5 }}>
                            {/* Per-account "Last Imported"
                                                    with a single-account refresh
                                                    button. Plaid's
                                                    transactionsRefresh works at
                                                    the item level, so this scopes
                                                    to the one account's own item;
                                                    see handleRefreshSingleAccount. */}
                            {renderLastImported(
                              lastImportByAccount[acctId(account)],
                              [account.item_id],
                              account.source !== "manual",
                              acctId(account)
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    {/* Right side: Current Balance and Period Deposits */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        ml: 2,
                        minWidth: 150,
                      }}
                    >
                      {loading ? (
                        <CircularProgress
                          size={20}
                          sx={{
                            display: "block",
                            ml: "auto",
                          }}
                        />
                      ) : (
                        <Typography variant="h6" color="primary">
                          {formatCurrency(
                            account.balances?.available ??
                              account.balances?.current ??
                              0
                          )}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Available Balance
                      </Typography>
                      {!loading && account.balances?.current != null && (
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {formatCurrency(account.balances.current)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Current Balance
                          </Typography>
                        </>
                      )}
                      <Tooltip
                        title={renderDepositsTooltip(account.account_id)}
                        arrow
                        placement="left"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "rgba(33, 33, 33, 0.95)",
                              maxWidth: "none",
                              "& .MuiTooltip-arrow": {
                                color: "rgba(33, 33, 33, 0.95)",
                              },
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            mt: 1,
                            textAlign: "right",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.8 },
                          }}
                        >
                          {loadingTransactions ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Typography
                              variant="body1"
                              color="success.main"
                              fontWeight="medium"
                            >
                              +
                              {formatCurrency(
                                getAccountDeposits(account.account_id)
                              )}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Period Deposits
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        <Tooltip title="Import CSV transactions">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => setImportingAccount(account)}
                          >
                            <UploadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {account.source === "manual" && (
                          <Tooltip
                            title={
                              agentJobAccountId === account.account_id
                                ? agentJobStatus || "Auto-importing…"
                                : "Auto-import via local agent"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={
                                  agentJobAccountId === account.account_id
                                }
                                onClick={() => handleAgentImport(account)}
                              >
                                {agentJobAccountId === account.account_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SmartToyIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="View import history">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenImportHistory(account)}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {account.source === "manual" ? (
                          <>
                            <Tooltip title="Edit name & balance">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openEditManualAccount(account)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove account">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteManualAccount(account)
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Disconnect account">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeletePlaidConnection(account.item_id)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        {/* Add Account Dialog (triggered by FAB) */}
        <Dialog
          open={isAddAccountDialogOpen}
          onClose={handleAddAccountClose}
          fullWidth
          maxWidth="xs"
          sx={{
            "& .MuiDialog-container": {
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        >
          <DialogTitle>Add Account</DialogTitle>
          <DialogContent>
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton onClick={handleAddBank} disabled={!ready}>
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Bank" secondary="Connect via Plaid" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleAddAccountClose();
                    // Direct-Connect banks are linked from the Prime Agent section in
                    // Settings, which holds the encrypted-credential flow.
                    window.location.assign("/settings");
                  }}
                >
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Bank (Direct Connect)"
                    secondary="Plaid can't reach it — link via Prime Agent"
                  />
                </ListItemButton>
              </ListItem>
              <Divider />
              {MANUAL_WALLET_PROVIDERS.map((provider) => (
                <ListItem disablePadding key={provider.id}>
                  <ListItemButton
                    onClick={() => handleWalletProviderSelect(provider)}
                  >
                    <ListItemIcon>
                      <AccountBalanceWalletIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={provider.label} />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
              <ListItem disablePadding>
                <ListItemButton onClick={handlePaymentProcessorToggle}>
                  <ListItemIcon>
                    <PaymentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Payment Processor" />
                  {isPaymentProcessorExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={isPaymentProcessorExpanded} timeout="auto">
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectPaymentProcessor("stripe")}
                      disabled={connectingStripe}
                    >
                      <ListItemIcon>
                        <CreditCardIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={connectingStripe ? "Connecting..." : "Stripe"}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton disabled>
                      <ListItemIcon>
                        <LockIcon fontSize="small" color="disabled" />
                      </ListItemIcon>
                      <ListItemText primary="PayPal" />
                      <Chip label="Coming Soon" size="small" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton disabled>
                      <ListItemIcon>
                        <LockIcon fontSize="small" color="disabled" />
                      </ListItemIcon>
                      <ListItemText primary="Zoho Payments" />
                      <Chip label="Coming Soon" size="small" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton disabled>
                      <ListItemIcon>
                        <LockIcon fontSize="small" color="disabled" />
                      </ListItemIcon>
                      <ListItemText primary="Authorize.net" />
                      <Chip label="Coming Soon" size="small" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddAccountClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <ToggleButtonGroup
          value={pageView}
          exclusive
          size="small"
          color="primary"
          onChange={(_, value) => {
            if (!value) return;
            setPageView(value);
            const params = new URLSearchParams(searchParams);
            if (value === "timeline") {
              params.set("view", "timeline");
            } else {
              params.delete("view");
            }
            setSearchParams(params, { replace: true });
          }}
        >
          <ToggleButton value="accounts">Accounts</ToggleButton>
          <ToggleButton value="timeline">Timeline</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {pageView === "timeline" && <AccountCoverageTimeline />}

      {pageView === "accounts" && (
        <>
          {/* Date Range Picker and Total Deposits */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 3,
              flexWrap: "wrap",
              alignItems: "stretch",
            }}
          >
            {/* Date Range Picker */}
            <Paper sx={{ p: 2, display: "flex", gap: 2, alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Quick Select</InputLabel>
                <Select
                  value={datePreset}
                  label="Quick Select"
                  onChange={(e) => handleDatePresetChange(e.target.value)}
                >
                  <MenuItem value="">Custom</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="ytd">Year to Date</MenuItem>
                  <MenuItem value="last_year">Last Year</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Start Date"
                type="date"
                value={pendingStartDate}
                onChange={(e) => {
                  setPendingStartDate(e.target.value);
                  setDatePreset("");
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                to
              </Typography>
              <TextField
                label="End Date"
                type="date"
                value={pendingEndDate}
                onChange={(e) => {
                  setPendingEndDate(e.target.value);
                  setDatePreset("");
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Button
                variant={hasUnappliedDateChanges ? "contained" : "outlined"}
                onClick={handleApplyDateFilter}
                disabled={!hasUnappliedDateChanges}
                startIcon={<FilterAltIcon />}
                size="small"
              >
                Apply
              </Button>
            </Paper>

            {/* Total Deposits Card */}
            <Card sx={{ minWidth: 200, ml: "auto" }}>
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <TrendingUpIcon color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Total Deposits
                  </Typography>
                </Box>
                {loadingTransactions ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography
                    variant="h5"
                    color="success.main"
                    fontWeight="bold"
                  >
                    {formatCurrency(totalDeposits)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Monthly Deposits Timeline Chart */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Monthly Deposits by Account</Typography>
              <ToggleButtonGroup
                value={chartStyle}
                exclusive
                onChange={(_, value) => value && setChartStyle(value)}
                size="small"
                color="primary"
                sx={{
                  "& .MuiToggleButton-root.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                }}
              >
                <ToggleButton value="curved">
                  <Tooltip title="Curved Line">
                    <ShowChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="straight">
                  <Tooltip title="Straight Line">
                    <TimelineIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="bar">
                  <Tooltip title="Bar Chart">
                    <BarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="area">
                  <Tooltip title="Area Chart">
                    <StackedBarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Tabs
              value={timelineTab}
              onChange={(_, value) => setTimelineTab(value)}
              sx={{
                mb: 2,
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  minHeight: 40,
                  color: "text.secondary",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 600,
                },
              }}
            >
              <Tab label="Checking" value="checking" />
              <Tab label="Savings" value="savings" />
              <Tab label="Credit Cards" value="credit" />
              <Tab label="All Accounts" value="all" />
            </Tabs>
            {Object.entries(updateTokensByItem).map(([itemId, token]) => (
              <PlaidUpdateLauncher
                key={`${itemId}:${token.slice(-10)}`}
                ref={(handle) => {
                  updateLauncherRefs.current[itemId] = handle;
                }}
                token={token}
                itemId={itemId}
                env={plaidEnvironment}
                onSuccess={() => {
                  handleUpdateLauncherOpened();
                  handleUpdateLauncherExited();
                }}
                onExit={(err, metadata) => {
                  console.debug("[PlaidUpdateLauncher] exit", {
                    err,
                    metadata,
                  });
                  handleUpdateLauncherExited();
                }}
                onEvent={(eventName, metadata) => {
                  console.debug("[PlaidUpdateLauncher] event", {
                    eventName,
                    metadata,
                  });
                  if (eventName === "OPEN") handleUpdateLauncherOpened();
                }}
              />
            ))}
            {/* Edit-connection launchers (account_selection_enabled update mode),
            one per institution item the user has clicked "Edit Plaid
            connection" for. The ref callback re-runs when the handle becomes
            ready, so we auto-open there for the pending item. */}
            {Object.entries(editConnectionTokensByItem).map(
              ([itemId, token]) => (
                <PlaidUpdateLauncher
                  key={`edit:${itemId}:${token.slice(-10)}`}
                  ref={(handle) => {
                    editConnectionLauncherRefs.current[itemId] = handle;
                    if (handle?.ready && editingConnectionItemId === itemId) {
                      console.log(
                        "[AccountsPage] edit-connection launcher ready (ref), opening",
                        { itemId }
                      );
                      setEditingConnectionItemId(null);
                      handle.open();
                    }
                  }}
                  token={token}
                  itemId={itemId}
                  env={plaidEnvironment}
                  onSuccess={() => {
                    console.debug(
                      "[PlaidUpdateLauncher] edit-connection success",
                      {
                        itemId,
                      }
                    );
                    handleEditConnectionFinished();
                  }}
                  onExit={(err, metadata) => {
                    console.debug(
                      "[PlaidUpdateLauncher] edit-connection exit",
                      {
                        itemId,
                        err,
                        metadata,
                      }
                    );
                    handleEditConnectionFinished();
                  }}
                  onEvent={(eventName, metadata) => {
                    console.debug(
                      "[PlaidUpdateLauncher] edit-connection event",
                      {
                        itemId,
                        eventName,
                        metadata,
                      }
                    );
                  }}
                />
              )
            )}
            {itemsNeedingUpdate.length > 0 && (
              <Alert
                severity="warning"
                sx={{
                  mt: 1,
                  alignItems: "center",
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <strong>Action needed:</strong>{" "}
                    {itemsNeedingUpdate.length === 1
                      ? "1 connected account needs"
                      : `${itemsNeedingUpdate.length} connected accounts need`}{" "}
                    you to re-authenticate. Transaction history is preserved.
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {itemsNeedingUpdate.map((it) => (
                      <Button
                        key={it.item_id}
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={updatingItemId === it.item_id}
                        onClick={() => handleUpdateItem(it.item_id)}
                      >
                        {updatingItemId === it.item_id
                          ? "Starting…"
                          : `Update ${it.institution_name || "account"}`}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Alert>
            )}
            {error ? (
              <Alert
                severity="error"
                sx={{
                  cursor: ready && linkToken ? "pointer" : "default",
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
                onClick={() => {
                  if (ready && linkToken) {
                    open();
                  }
                }}
              >
                There is an error within our system.{" "}
                {ready && linkToken ? (
                  <Box
                    component="span"
                    sx={{
                      textDecoration: "underline",
                      fontWeight: 600,
                      "&:hover": { color: "error.dark" },
                    }}
                  >
                    Please click here to relink your accounts.
                  </Box>
                ) : (
                  "Please try again later."
                )}
              </Alert>
            ) : plaidExitIssue ? (
              <Alert
                severity="warning"
                sx={{ "& .MuiAlert-message": { width: "100%" } }}
                action={
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const payload = JSON.stringify(
                            {
                              status: plaidExitIssue.status,
                              occurredAt: plaidExitIssue.occurredAt,
                              metadata: plaidExitIssue.metadata,
                              error: plaidExitIssue.error,
                            },
                            null,
                            2
                          );
                          await navigator.clipboard.writeText(payload);
                          console.debug(
                            "[DEBUG] Plaid support payload copied to clipboard"
                          );
                        } catch (copyError) {
                          console.error(
                            "Failed to copy Plaid support payload:",
                            copyError
                          );
                        }
                      }}
                    >
                      Copy support payload
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!ready || !linkToken}
                      onClick={() => {
                        console.debug(
                          "[DEBUG] Retrying Plaid Link after requires_credentials"
                        );
                        open();
                      }}
                    >
                      Try again
                    </Button>
                  </Box>
                }
              >
                Plaid needs credentials again (PIN/MFA). Try again or copy the
                support payload.
              </Alert>
            ) : loadingTransactions || loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredTimelineData &&
              filteredTimelineData.datasets.length > 0 ? (
              <Box sx={{ height: 300 }}>
                {chartStyle === "bar" ? (
                  <Bar
                    data={{
                      ...filteredTimelineData,
                      datasets: filteredTimelineData.datasets.map(
                        (dataset) => ({
                          ...dataset,
                          // Use borderColor as backgroundColor for solid bars
                          backgroundColor: dataset.borderColor,
                        })
                      ),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom" as const,
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            generateLabels: buildTimelineLegendLabels,
                          },
                          onClick: (_e: any, legendItem: any, legend: any) => {
                            if (
                              legendItem.datasetIndex == null ||
                              legendItem.datasetIndex < 0
                            ) {
                              return;
                            }
                            const dataset =
                              legend.chart.data.datasets[
                                legendItem.datasetIndex
                              ];
                            if (dataset?.accountId) {
                              toggleChartAccountVisibility(dataset.accountId);
                            }
                          },
                        },
                        tooltip: depositsChartTooltip,
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value: any) => {
                              if (value >= 1000) {
                                return `$${(value / 1000).toFixed(0)}k`;
                              }
                              return `$${value}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Line
                    data={{
                      ...filteredTimelineData,
                      datasets: filteredTimelineData.datasets.map(
                        (dataset) => ({
                          ...dataset,
                          tension: chartStyle === "curved" ? 0.4 : 0,
                          fill: chartStyle === "area",
                          backgroundColor:
                            chartStyle === "area"
                              ? dataset.borderColor
                                  ?.toString()
                                  .replace(")", ", 0.2)")
                              : dataset.backgroundColor,
                        })
                      ),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom" as const,
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            generateLabels: buildTimelineLegendLabels,
                          },
                          onClick: (_e: any, legendItem: any, legend: any) => {
                            if (
                              legendItem.datasetIndex == null ||
                              legendItem.datasetIndex < 0
                            ) {
                              return;
                            }
                            const dataset =
                              legend.chart.data.datasets[
                                legendItem.datasetIndex
                              ];
                            if (dataset?.accountId) {
                              toggleChartAccountVisibility(dataset.accountId);
                            }
                          },
                        },
                        tooltip: depositsChartTooltip,
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          stacked: chartStyle === "area",
                          ticks: {
                            callback: (value: any) => {
                              if (value >= 1000) {
                                return `$${(value / 1000).toFixed(0)}k`;
                              }
                              return `$${value}`;
                            },
                          },
                        },
                        x: {
                          stacked: chartStyle === "area",
                        },
                      },
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2">
                  No {timelineTab === "all" ? "" : `${timelineTab} `}accounts
                  with deposit data
                </Typography>
                {timelineTab !== "all" && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Try "All Accounts" tab
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Sync Status Section */}
          <Collapse in={showSyncStatus}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  <SyncIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Transaction Sync Status
                </Typography>
                <Button size="small" onClick={() => setShowSyncStatus(false)}>
                  Close
                </Button>
              </Box>
              {syncStatus.length === 0 ? (
                <Typography color="text.secondary">
                  No connected accounts found.
                </Typography>
              ) : (
                <List dense>
                  {syncStatus.map((item) => (
                    <ListItem
                      key={item.item_id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                      }}
                      secondaryAction={
                        <Tooltip title="Force refresh historical transactions">
                          <span>
                            <IconButton
                              edge="end"
                              onClick={() => handleRefreshAccount(item.item_id)}
                              disabled={refreshingItems.has(item.item_id)}
                            >
                              {refreshingItems.has(item.item_id) ? (
                                <CircularProgress size={20} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemText
                        primaryTypographyProps={{ component: "div" }}
                        secondaryTypographyProps={{ component: "div" }}
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="medium">
                              {item.institution_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "monospace",
                                color: "text.secondary",
                              }}
                            >
                              ({item.item_id.slice(-8).toUpperCase()})
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {item.error ? (
                              <Alert severity="error" sx={{ py: 0 }}>
                                {item.error}
                              </Alert>
                            ) : (
                              <>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Total Transactions:{" "}
                                  {item.transactions.total_count}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Date Range:{" "}
                                  {item.transactions.oldest_date
                                    ? `${new Date(
                                        item.transactions.oldest_date
                                      ).toLocaleDateString("en-US", {
                                        timeZone: "UTC",
                                      })} - ${new Date(
                                        item.transactions.newest_date || ""
                                      ).toLocaleDateString("en-US", {
                                        timeZone: "UTC",
                                      })}`
                                    : "No transactions yet"}
                                </Typography>
                                {item.transactions.last_successful_update && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Last Sync:{" "}
                                    {new Date(
                                      item.transactions.last_successful_update
                                    ).toLocaleString()}
                                  </Typography>
                                )}
                                {item.transactions.total_count === 0 && (
                                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                                    Historical data is still syncing. This can
                                    take 24-48 hours after connecting.
                                  </Alert>
                                )}
                                {item.transactions.oldest_date &&
                                  new Date(item.transactions.oldest_date) >
                                    new Date(
                                      Date.now() - 30 * 24 * 60 * 60 * 1000
                                    ) && (
                                    <Alert
                                      severity="warning"
                                      sx={{ mt: 1, py: 0 }}
                                    >
                                      Only recent transactions available.
                                      Historical data may still be syncing.
                                    </Alert>
                                  )}
                              </>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Note: After reconnecting an account, historical transaction data
                can take 24-48 hours to fully sync from the financial
                institution.
              </Typography>
            </Paper>
          </Collapse>

          {/* Test Credentials Info - Only show in demo mode */}
          {isDemoMode && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setShowTestInfo(!showTestInfo)}
                startIcon={
                  showTestInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                sx={{ mb: 1 }}
              >
                {showTestInfo ? "Hide" : "Show"} Sandbox Test Credentials
              </Button>
              <Collapse in={showTestInfo}>
                <PlaidTestInfo />
              </Collapse>
            </Box>
          )}

          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Accounts</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {(() => {
                  const allItemIds = syncStatus.map((s) => s.item_id);
                  const anyRefreshing = allItemIds.some((id) =>
                    refreshingItems.has(id)
                  );
                  return (
                    <Tooltip
                      title={anyRefreshing ? "Refreshing…" : "Refresh All"}
                      arrow
                    >
                      <span>
                        <ToggleButton
                          value="refreshAll"
                          size="small"
                          disabled={anyRefreshing || allItemIds.length === 0}
                          onClick={() => handleRefreshItems(allItemIds)}
                          sx={{ p: 0.75 }}
                        >
                          {anyRefreshing ? (
                            <CircularProgress size={18} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </ToggleButton>
                      </span>
                    </Tooltip>
                  );
                })()}
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                      >
                        Only Show Shared Accounts
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block" }}>
                        Accounts you've shared with other people via a group. To
                        share one, click the share icon on an organization,
                        bank, or account and choose who to share it with. Enable
                        this toggle to show only those shared accounts.
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <ToggleButton
                    value="sharedOnly"
                    size="small"
                    selected={sharedOnly}
                    onChange={() => setSharedOnly((prev) => !prev)}
                    sx={{ p: 0.75 }}
                  >
                    <ShareIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
                {!loading && !loadingStripe && groupedAccounts.length > 0 && (
                  <Tooltip
                    title={
                      expandedAccordions.size === groupedAccounts.length
                        ? "Collapse All"
                        : "Expand All"
                    }
                    arrow
                  >
                    <ToggleButton
                      value="expand"
                      size="small"
                      onClick={() => {
                        if (
                          expandedAccordions.size === groupedAccounts.length
                        ) {
                          setExpandedAccordions(new Set());
                        } else {
                          const allIds = new Set(
                            groupedAccounts.map((g) => g.institutionId)
                          );
                          setExpandedAccordions(allIds);
                        }
                      }}
                      sx={{ p: 0.75 }}
                    >
                      {expandedAccordions.size === groupedAccounts.length ? (
                        <UnfoldLessIcon fontSize="small" />
                      ) : (
                        <UnfoldMoreIcon fontSize="small" />
                      )}
                    </ToggleButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {loading || loadingStripe ? (
              <Box>
                {/* Skeleton accordions mimicking bank groups */}
                {[1, 2, 3].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      mb: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    {/* Accordion header skeleton */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width={120} height={28} />
                        <Skeleton
                          variant="rounded"
                          width={70}
                          height={22}
                          sx={{ borderRadius: 3 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                          mr: 4,
                        }}
                      >
                        <Box sx={{ textAlign: "right" }}>
                          <Skeleton variant="text" width={60} height={16} />
                          <Skeleton variant="text" width={80} height={24} />
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Skeleton variant="text" width={70} height={16} />
                          <Skeleton variant="text" width={90} height={24} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : visibleAccounts.length === 0 &&
              visibleStripeAccounts.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <AccountBalanceIcon
                  sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {sharedOnly ? "No shared accounts" : "No accounts connected"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {sharedOnly
                    ? "Share an account to see it here."
                    : "Connect your bank accounts or payment processors to start tracking your finances"}
                </Typography>
                {!sharedOnly && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddAccountClick}
                    size="large"
                  >
                    Add Account
                  </Button>
                )}
              </Box>
            ) : (
              <Box>
                {!sharedOnly && (
                  <ConnectChecklist
                    accounts={accounts}
                    refreshKey={accounts.length}
                    onConnectBank={handleAddBank}
                    onConnectStripe={handleConnectStripe}
                    onAddWallet={(pid) => {
                      const p = MANUAL_WALLET_PROVIDERS.find(
                        (x) => x.id === pid
                      );
                      if (p) handleWalletProviderSelect(p);
                    }}
                  />
                )}
                {/* Grouping tabs: Organization (default) vs Account (institution). */}
                <Tabs
                  value={accountsGroupBy}
                  onChange={(_, value) =>
                    handleAccountsGroupByChange(
                      value as "organization" | "account"
                    )
                  }
                  sx={{ mb: 2, minHeight: 40 }}
                >
                  <Tab
                    value="organization"
                    label="Organization"
                    icon={<DomainIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 40, textTransform: "none" }}
                  />
                  <Tab
                    value="account"
                    label="Account"
                    icon={<AccountBalanceIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 40, textTransform: "none" }}
                  />
                </Tabs>

                {/* Account view: flat list grouped by financial institution. */}
                {accountsGroupBy === "account" && (
                  <Box sx={{ mb: 2 }}>
                    {groupedAccounts.map((group) =>
                      renderInstitutionGroup(group)
                    )}
                  </Box>
                )}

                {/* Organization Groups */}
                {accountsGroupBy === "organization" &&
                  accountsByOrganization.map((orgGroup) => {
                    const orgKey = orgGroup.organization?.id || "unassigned";
                    const orgLastImport = lastImportForAccounts(
                      orgGroup.accounts
                    );
                    const orgItemIds = Array.from(
                      new Set(orgGroup.accounts.map((a) => a.item_id))
                    );
                    return (
                      <Accordion
                        key={orgKey}
                        expanded={!collapsedOrgs.has(orgKey)}
                        onChange={(_, isExpanded) => {
                          setCollapsedOrgs((prev) => {
                            const next = new Set(prev);
                            if (isExpanded) {
                              next.delete(orgKey);
                            } else {
                              next.add(orgKey);
                            }
                            return next;
                          });
                        }}
                        sx={{
                          mb: 3,
                          "&:before": { display: "none" },
                          boxShadow: "none",
                          border: "none",
                          background: "transparent",
                        }}
                      >
                        {/* Organization Header */}
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            px: 0,
                            borderBottom: "2px solid",
                            borderColor:
                              orgGroup.organization?.color || "divider",
                            "& .MuiAccordionSummary-content": {
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              my: 1,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              bgcolor:
                                orgGroup.organization?.color || "grey.400",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {orgGroup.organization ? (
                              <DomainIcon
                                sx={{ fontSize: 18, color: "white" }}
                              />
                            ) : (
                              <AccountBalanceIcon
                                sx={{ fontSize: 18, color: "white" }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Tooltip
                                title={renderGroupRecentTransactionsTooltip(
                                  orgGroup.accounts
                                )}
                                arrow
                                placement="right"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "rgba(33, 33, 33, 0.95)",
                                      maxWidth: "none",
                                      "& .MuiTooltip-arrow": {
                                        color: "rgba(33, 33, 33, 0.95)",
                                      },
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  fontWeight="bold"
                                  sx={{
                                    textDecoration: "underline dotted",
                                    textUnderlineOffset: "4px",
                                  }}
                                >
                                  {orgGroup.organization?.name ||
                                    "Unassigned Accounts"}
                                </Typography>
                              </Tooltip>
                              {orgGroup.organization?.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  — {orgGroup.organization.description}
                                </Typography>
                              )}
                            </Box>
                            {renderLastImported(orgLastImport, orgItemIds)}
                          </Box>
                          <Chip
                            label={`${orgGroup.accounts.length + orgGroup.stripeAccounts.length} account${
                              orgGroup.accounts.length +
                                orgGroup.stripeAccounts.length !==
                              1
                                ? "s"
                                : ""
                            }`}
                            size="small"
                            sx={{
                              bgcolor:
                                orgGroup.organization?.color || "grey.400",
                              color: "white",
                              fontWeight: "medium",
                            }}
                          />
                          {(() => {
                            const gb = groupBalances(orgGroup.accounts);
                            return (
                              <Box
                                sx={{ ml: "auto", mr: 1, textAlign: "right" }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  {formatCurrency(gb.available)} available
                                </Typography>
                                {gb.hasCurrent && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {formatCurrency(gb.current)} current
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}
                          <Tooltip
                            title={`Shared with ${groupShareCount(
                              orgGroup.accounts
                            )} ${
                              groupShareCount(orgGroup.accounts) === 1
                                ? "person/group"
                                : "people/groups"
                            } — manage sharing for this organization`}
                          >
                            <IconButton
                              component="div"
                              role="button"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openManageShare(
                                  orgGroup.organization?.name ||
                                    "Unassigned Accounts",
                                  orgGroup.accounts.map(acctId)
                                );
                              }}
                              sx={{ mr: 1 }}
                            >
                              <Badge
                                badgeContent={groupShareCount(
                                  orgGroup.accounts
                                )}
                                showZero
                                color="primary"
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: "0.55rem",
                                    height: 14,
                                    minWidth: 14,
                                  },
                                }}
                              >
                                <ShareIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </AccordionSummary>

                        <AccordionDetails sx={{ px: 0, pt: 1.5 }}>
                          {/* Bank Accounts within this Organization - Grouped by
                          Institution. Build the institution grouping from ONLY
                          this org's member accounts (orgGroup.accounts), so an
                          account appears under exactly one org bucket and never
                          leaks into a shared institution group across orgs. */}
                          {buildInstitutionGroups(orgGroup.accounts).map(
                            (group) => renderInstitutionGroup(group)
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                {/* Stripe Accounts (shown outside organization grouping for now) */}
                {stripeAccounts.filter(
                  (sa) =>
                    !organizations.some(
                      (org) => accountOrganizations[sa.id] === org.id
                    )
                ).length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: "text.secondary" }}
                    >
                      Payment Processors
                    </Typography>
                  </Box>
                )}
                {stripeAccounts
                  .filter(
                    (sa) =>
                      !organizations.some(
                        (org) => accountOrganizations[sa.id] === org.id
                      )
                  )
                  .map((stripeAccount) => (
                    <ListItem
                      key={stripeAccount.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        "&:last-child": { mb: 0 },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: "#635bff",
                            fontSize: "1.2rem",
                          }}
                        >
                          <CreditCardIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ ml: 1, flex: 1 }}
                        primaryTypographyProps={{ component: "div" }}
                        secondaryTypographyProps={{ component: "div" }}
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="h6">
                              {stripeAccount.business_name || "Stripe Account"}
                            </Typography>
                            <Chip
                              label="Stripe"
                              size="small"
                              sx={{
                                bgcolor: "#635bff",
                                color: "white",
                                height: 20,
                                fontSize: "0.7rem",
                              }}
                            />
                            {!stripeAccount.livemode && (
                              <Chip
                                label="Test"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {stripeAccount.email && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {stripeAccount.email}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                mt: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Connected{" "}
                                {new Date(
                                  stripeAccount.connected_at
                                ).toLocaleDateString("en-US", {
                                  timeZone: "UTC",
                                })}
                              </Typography>
                              {/* Oldest record date */}
                              {getOldestStripeRecordDate(
                                stripeAccount.stripe_user_id
                              ) && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Records since:{" "}
                                  {new Date(
                                    getOldestStripeRecordDate(
                                      stripeAccount.stripe_user_id
                                    )!
                                  ).toLocaleDateString("en-US", {
                                    timeZone: "UTC",
                                  })}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      {/* Right side: Balance and Period Deposits */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          ml: 2,
                          minWidth: 150,
                        }}
                      >
                        {stripeAccount.balance ? (
                          <>
                            <Typography variant="h6" color="primary">
                              {formatCurrency(stripeAccount.balance.available)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Available Balance
                            </Typography>
                            {stripeAccount.balance.pending > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                +{formatCurrency(stripeAccount.balance.pending)}{" "}
                                pending
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Balance unavailable
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, textAlign: "right" }}>
                          {loadingTransactions ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Typography
                              variant="body1"
                              color="success.main"
                              fontWeight="medium"
                            >
                              +
                              {formatCurrency(
                                getStripeAccountDeposits(
                                  stripeAccount.stripe_user_id
                                )
                              )}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Period Revenue
                          </Typography>
                        </Box>
                        <Tooltip title="Disconnect Stripe">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDisconnectStripe(stripeAccount.id)
                            }
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Edit Account Name Dialog */}
      <Dialog
        open={editingAccount !== null}
        onClose={() => setEditingAccount(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Account Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a custom name for this account. This will appear in dropdowns
            and lists.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Custom Name"
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            placeholder={editingAccount?.name || "Enter custom name"}
            helperText="Leave blank to use the default name"
          />
          {editingAccount && (
            <Box sx={{ mt: 2, p: 1, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Default name: {editingAccount.name}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Account ID: {editingAccount.account_id.slice(-8).toUpperCase()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingAccount(null)}>Cancel</Button>
          <Button
            onClick={handleSaveName}
            variant="contained"
            disabled={savingName}
          >
            {savingName ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog
        open={importingAccount !== null}
        onClose={() => {
          setImportingAccount(null);
          setImportFile(null);
          setImportPreview([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import Transactions for {importingAccount?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file with transactions. The file should have columns
            for Date, Amount, and Description. Transactions that already exist
            via Plaid will not be duplicated.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              Select CSV File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {importFile.name}
              </Typography>
            )}
          </Box>

          {importPreview.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview (first 10 transactions):
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th style={{ padding: 8, textAlign: "left" }}>Date</th>
                      <th style={{ padding: 8, textAlign: "right" }}>Amount</th>
                      <th style={{ padding: 8, textAlign: "left" }}>
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((txn, idx) => (
                      <tr key={idx} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: 8 }}>{txn.date}</td>
                        <td
                          style={{
                            padding: 8,
                            textAlign: "right",
                            color: txn.amount < 0 ? "green" : "inherit",
                          }}
                        >
                          {formatCurrency(txn.amount)}
                        </td>
                        <td style={{ padding: 8 }}>{txn.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Manual imports will be marked separately from Plaid transactions and
            will not overwrite any existing Plaid data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImportingAccount(null);
              setImportFile(null);
              setImportPreview([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImportTransactions}
            variant="contained"
            disabled={importing || !importFile}
            startIcon={
              importing ? <CircularProgress size={20} /> : <UploadIcon />
            }
          >
            {importing ? "Importing..." : "Import Transactions"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog
        open={importHistoryAccount !== null}
        onClose={() => {
          setImportHistoryAccount(null);
          setImportHistory([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import History for {importHistoryAccount?.name}
        </DialogTitle>
        <DialogContent>
          {loadingImportHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : importHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No import history yet for this account.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Imported</TableCell>
                  <TableCell align="right">Skipped</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Oldest Record</TableCell>
                  <TableCell>Newest Record</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importHistory.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {new Date(entry.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={entry.status}
                        color={entry.status === "success" ? "success" : "error"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{entry.imported_count}</TableCell>
                    <TableCell align="right">{entry.skipped_count}</TableCell>
                    <TableCell align="right">{entry.total_count}</TableCell>
                    <TableCell>
                      {entry.oldest_date
                        ? new Date(entry.oldest_date).toLocaleDateString(
                            "en-US",
                            { timeZone: "UTC" }
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {entry.newest_date
                        ? new Date(entry.newest_date).toLocaleDateString(
                            "en-US",
                            { timeZone: "UTC" }
                          )
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImportHistoryAccount(null);
              setImportHistory([]);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transactions Viewer Dialog */}
      <Dialog
        open={transactionsViewerOpen}
        onClose={() => {
          setTransactionsViewerOpen(false);
          setTransactionsViewerData([]);
          setHighlightTransactionDate(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{transactionsViewerTitle}</DialogTitle>
        <DialogContent>
          {transactionsViewerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : transactionsViewerData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No transactions found.
            </Typography>
          ) : (
            <>
              {/* Filter Controls */}
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search transactions…"
                  value={txnSearch}
                  onChange={(e) => setTxnSearch(e.target.value)}
                  sx={{ minWidth: 220, flex: "1 1 220px" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={txnDateFrom}
                  onChange={(e) => setTxnDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={txnDateTo}
                  onChange={(e) => setTxnDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={txnFilterSource}
                    label="Source"
                    onChange={(e) => setTxnFilterSource(e.target.value)}
                  >
                    <MenuItem value="">All Sources</MenuItem>
                    <MenuItem value="plaid">Plaid</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Merchant</InputLabel>
                  <Select
                    value={txnFilterMerchant}
                    label="Merchant"
                    onChange={(e) => setTxnFilterMerchant(e.target.value)}
                  >
                    <MenuItem value="">All Merchants</MenuItem>
                    {uniqueMerchants.map((merchant) => (
                      <MenuItem key={merchant} value={merchant}>
                        {merchant}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={txnFilterCategory}
                    label="Category"
                    onChange={(e) => setTxnFilterCategory(e.target.value)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {uniqueCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(txnFilterSource ||
                  txnFilterMerchant ||
                  txnFilterCategory ||
                  txnSearch ||
                  txnDateFrom ||
                  txnDateTo) && (
                  <Button
                    size="small"
                    onClick={() => resetTxnFilters("all")}
                    variant="outlined"
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sortDirection={
                        txnSortColumn === "date" ? txnSortDirection : false
                      }
                    >
                      <TableSortLabel
                        active={txnSortColumn === "date"}
                        direction={
                          txnSortColumn === "date" ? txnSortDirection : "asc"
                        }
                        onClick={() => handleTxnSort("date")}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        txnSortColumn === "name" ? txnSortDirection : false
                      }
                    >
                      <TableSortLabel
                        active={txnSortColumn === "name"}
                        direction={
                          txnSortColumn === "name" ? txnSortDirection : "asc"
                        }
                        onClick={() => handleTxnSort("name")}
                      >
                        Description
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        txnSortColumn === "merchant_name"
                          ? txnSortDirection
                          : false
                      }
                    >
                      <TableSortLabel
                        active={txnSortColumn === "merchant_name"}
                        direction={
                          txnSortColumn === "merchant_name"
                            ? txnSortDirection
                            : "asc"
                        }
                        onClick={() => handleTxnSort("merchant_name")}
                      >
                        Merchant
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        txnSortColumn === "category" ? txnSortDirection : false
                      }
                    >
                      <TableSortLabel
                        active={txnSortColumn === "category"}
                        direction={
                          txnSortColumn === "category"
                            ? txnSortDirection
                            : "asc"
                        }
                        onClick={() => handleTxnSort("category")}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="right"
                      sortDirection={
                        txnSortColumn === "amount" ? txnSortDirection : false
                      }
                    >
                      <TableSortLabel
                        active={txnSortColumn === "amount"}
                        direction={
                          txnSortColumn === "amount" ? txnSortDirection : "asc"
                        }
                        onClick={() => handleTxnSort("amount")}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell
                      sortDirection={
                        txnSortColumn === "source" ? txnSortDirection : false
                      }
                    >
                      <TableSortLabel
                        active={txnSortColumn === "source"}
                        direction={
                          txnSortColumn === "source" ? txnSortDirection : "asc"
                        }
                        onClick={() => handleTxnSort("source")}
                      >
                        Source
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((txn) => {
                    const isHighlighted =
                      highlightTransactionDate &&
                      txn.date === highlightTransactionDate;
                    return (
                      <TableRow
                        key={txn.transaction_id}
                        id={`txn-${txn.transaction_id}`}
                        sx={{
                          backgroundColor: isHighlighted
                            ? "warning.light"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: isHighlighted
                              ? "warning.main"
                              : "action.hover",
                          },
                        }}
                        ref={
                          isHighlighted
                            ? (el) => {
                                if (el) {
                                  setTimeout(() => {
                                    el.scrollIntoView({
                                      behavior: "smooth",
                                      block: "center",
                                    });
                                  }, 100);
                                }
                              }
                            : undefined
                        }
                      >
                        <TableCell>
                          {new Date(txn.date).toLocaleDateString("en-US", {
                            timeZone: "UTC",
                          })}
                        </TableCell>
                        <TableCell>{txn.name}</TableCell>
                        <TableCell>{txn.merchant_name || "-"}</TableCell>
                        <TableCell>
                          {Array.isArray(txn.category)
                            ? txn.category.join(", ")
                            : txn.category || "-"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              txn.amount < 0 ? "success.main" : "error.main",
                            fontWeight: "medium",
                          }}
                        >
                          {txn.amount < 0 ? "+" : "-"}$
                          {Math.abs(txn.amount).toFixed(2)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: "text.secondary" }}
                        >
                          {runningBalanceByTxn[txn.transaction_id] != null
                            ? formatCurrency(
                                runningBalanceByTxn[txn.transaction_id]
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={txn.source === "manual" ? "Manual" : "Plaid"}
                            size="small"
                            color={
                              txn.source === "manual" ? "secondary" : "primary"
                            }
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {/* Pagination: page size, prev/next, first/last, jump-to-page */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1,
                  flexWrap: "wrap",
                  mt: 1,
                }}
              >
                <Button
                  size="small"
                  disabled={txnPage === 0}
                  onClick={() => setTxnPage(0)}
                >
                  « First
                </Button>
                <Button
                  size="small"
                  disabled={txnPage + 1 >= txnPageCount}
                  onClick={() => setTxnPage(txnPageCount - 1)}
                >
                  Last »
                </Button>
                <TextField
                  size="small"
                  label="Jump to page"
                  value={txnJumpPage}
                  onChange={(e) =>
                    setTxnJumpPage(e.target.value.replace(/\D/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && txnJumpPage) {
                      const p = Math.min(
                        Math.max(1, parseInt(txnJumpPage, 10)),
                        txnPageCount
                      );
                      setTxnPage(p - 1);
                      setTxnJumpPage("");
                    }
                  }}
                  sx={{ width: 120 }}
                  helperText={`of ${txnPageCount}`}
                />
                <TablePagination
                  component="div"
                  count={filteredSortedTransactions.length}
                  page={txnPage}
                  onPageChange={(_e, p) => setTxnPage(p)}
                  rowsPerPage={txnRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setTxnRowsPerPage(parseInt(e.target.value, 10));
                    setTxnPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100, 250]}
                  labelRowsPerPage="Per page"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
            {filteredSortedTransactions.length === transactionsViewerData.length
              ? `${transactionsViewerData.length} transactions`
              : `${filteredSortedTransactions.length} of ${transactionsViewerData.length} transactions`}
          </Typography>
          <Button
            onClick={() => {
              setTransactionsViewerOpen(false);
              setTransactionsViewerData([]);
              setHighlightTransactionDate(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleCloseDeleteAllDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete All Accounts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This removes every connected bank account, Stripe account, imported
            account history, and account-linked manual transactions for this
            Focus: Finance user.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Focus: Finance account password"
            type="password"
            value={deleteAllPassword}
            onChange={(event) => setDeleteAllPassword(event.target.value)}
            disabled={deletingAllAccounts}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteAllDialog}
            disabled={deletingAllAccounts}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAllAccounts}
            disabled={!deleteAllPassword.trim() || deletingAllAccounts}
            startIcon={
              deletingAllAccounts ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Delete All Accounts
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Buttons */}
      <FloatingActionMenu label="Account actions">
        <Tooltip title="Delete All Accounts" placement="left" arrow>
          <Fab
            color="error"
            size="large"
            onClick={handleOpenDeleteAllDialog}
            disabled={!hasConnectedAccounts || deletingAllAccounts}
            sx={{
              opacity: !hasConnectedAccounts || deletingAllAccounts ? 0.5 : 1,
            }}
          >
            {deletingAllAccounts ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <DeleteIcon />
            )}
          </Fab>
        </Tooltip>

        {/* Manage Organizations FAB */}
        <Tooltip title="Manage Organizations" placement="left" arrow>
          <Fab
            color="default"
            size="large"
            onClick={() => handleOpenOrgDialog()}
          >
            <BusinessIcon />
          </Fab>
        </Tooltip>

        {/* Sync FAB */}
        <Tooltip title="Sync Transactions" placement="left" arrow>
          <Fab
            color="secondary"
            size="large"
            onClick={handleCheckSyncStatus}
            disabled={loadingSyncStatus}
            sx={{
              opacity: loadingSyncStatus ? 0.5 : 1,
            }}
          >
            {loadingSyncStatus ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SyncIcon />
            )}
          </Fab>
        </Tooltip>

        {/* Add Account FAB */}
        <Tooltip title="Add Account" placement="left" arrow>
          <Fab color="primary" size="large" onClick={handleAddAccountClick}>
            <AddIcon />
          </Fab>
        </Tooltip>
      </FloatingActionMenu>

      {/* Manual wallet account creation dialog (Cash App, Venmo, Lyft, Uber) */}
      <Dialog
        open={Boolean(activeWalletProvider)}
        onClose={() => !creatingWallet && setActiveWalletProvider(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceWalletIcon color="success" />
          <Box component="span" sx={{ flexGrow: 1 }}>
            Add {activeWalletProvider?.label} Account
          </Box>
          <Tooltip title="Hand off to my AI agent — copies a prompt your agent runs against the Focus: Finance MCP server to add this account for you">
            <IconButton
              aria-label="Hand off to my AI agent"
              onClick={handleCopyWalletHandoff}
              size="small"
            >
              <SmartToyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {activeWalletProvider?.partnerNote} Instead, add{" "}
            {activeWalletProvider?.label} as a tracked account here: set your
            current balance and import your transaction history from a CSV
            export.
          </Alert>
          <TextField
            label="Account name"
            fullWidth
            value={walletName}
            onChange={(e) => setWalletName(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Current balance"
            fullWidth
            type="number"
            value={walletBalance}
            onChange={(e) => setWalletBalance(e.target.value)}
            placeholder="0.00"
            InputProps={{ startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> }}
            helperText="Optional — you can update this any time."
            sx={{ mb: 2 }}
          />
          <TextField
            label="Last 4 digits (optional)"
            fullWidth
            value={walletMask}
            onChange={(e) =>
              setWalletMask(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            sx={{ mb: 2 }}
          />
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Next: import your transaction history
          </Typography>
          <Box component="ol" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              {activeWalletProvider?.csvSource}
              {activeWalletProvider?.transactionsUrl && (
                <>
                  {" "}
                  <Link
                    href={activeWalletProvider.transactionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    Open {activeWalletProvider.label} transactions ↗
                  </Link>
                </>
              )}
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              After you click <strong>Create Account</strong>, the CSV import
              window opens automatically — upload the file there.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActiveWalletProvider(null)}
            disabled={creatingWallet}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateWalletAccount}
            disabled={creatingWallet || !walletName.trim()}
          >
            {creatingWallet ? "Creating…" : "Create Account"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit manual (Cash App) account: name + balance */}
      <Dialog
        open={editingManualAccount !== null}
        onClose={() => !savingManualAccount && setEditingManualAccount(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceWalletIcon color="success" />
          Edit Account
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Account name"
            fullWidth
            value={manualEditName}
            onChange={(e) => setManualEditName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            autoFocus
          />
          <TextField
            label="Current balance"
            fullWidth
            type="number"
            value={manualEditBalance}
            onChange={(e) => setManualEditBalance(e.target.value)}
            placeholder="0.00"
            InputProps={{ startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditingManualAccount(null)}
            disabled={savingManualAccount}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveManualAccount}
            disabled={savingManualAccount || !manualEditName.trim()}
          >
            {savingManualAccount ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Organization Management Dialog */}
      <Dialog
        open={orgDialogOpen}
        onClose={handleCloseOrgDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BusinessIcon color="secondary" />
          {editingOrg ? "Edit Organization" : "Manage Organizations"}
        </DialogTitle>
        <DialogContent>
          {!editingOrg && organizations.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Existing Organizations
              </Typography>
              <List dense>
                {organizations.map((org) => (
                  <ListItem
                    key={org.id}
                    secondaryAction={
                      <Box>
                        <IconButton
                          edge="end"
                          onClick={() => handleOpenOrgDialog(org)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteOrg(org.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          bgcolor: org.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DomainIcon sx={{ fontSize: 14, color: "white" }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={org.name}
                      secondary={org.description}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            {editingOrg ? "Edit Organization" : "Add New Organization"}
          </Typography>
          <TextField
            fullWidth
            label="Organization Name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g., Hill Family, The Portland Company"
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={orgDescription}
            onChange={(e) => setOrgDescription(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Optional description"
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Color
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[
                "#1976d2",
                "#388e3c",
                "#d32f2f",
                "#7b1fa2",
                "#f57c00",
                "#0288d1",
                "#689f38",
                "#c2185b",
                "#512da8",
                "#455a64",
              ].map((color) => (
                <Box
                  key={color}
                  onClick={() => setOrgColor(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: color,
                    cursor: "pointer",
                    border:
                      orgColor === color
                        ? "3px solid black"
                        : "3px solid transparent",
                    "&:hover": { opacity: 0.8 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrgDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveOrg}
            disabled={!orgName.trim() || savingOrg}
            color="secondary"
          >
            {savingOrg ? (
              <CircularProgress size={20} color="inherit" />
            ) : editingOrg ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ManageSharesModal
        open={Boolean(manageShare)}
        title={manageShare?.title || ""}
        accountIds={manageShare?.accountIds || []}
        groups={groups}
        onClose={() => setManageShare(null)}
        onChanged={() => {
          void loadAccounts();
        }}
      />
    </Container>
  );
};

export default AccountsPage;
